/**
 * Algolia-Powered Collection Page
 *
 * Progressive enhancement strategy:
 * - Server-rendered Shopify products provide SEO-friendly baseline
 * - Algolia enhances with instant filtering, sorting, and pagination
 * - Graceful fallback if Algolia not configured or errors occur
 *
 * Features:
 * - Instant filtering (no page reload)
 * - Multi-select facets (OR logic within facet, AND across facets)
 * - Sorting (relevance, price asc/desc, newest)
 * - Pagination with URL state
 * - Marketing blocks integration
 * - MTO product compatibility
 * - Analytics event publishing
 */
(function () {
  'use strict';

  // ───────────────────────────────────────────────────────────────────────────
  // Configuration & State
  // ───────────────────────────────────────────────────────────────────────────
  var config = window.__ALGOLIA_CONFIG;
  var searchClient = null;
  var collectionHandle = '';
  // var collectionId = '';
  var currentFilters = {};
  var currentSort = 'default';
  var currentPage = 0;
  var currentQuery = '';
  var hitsPerPage = 48;
  var lastResponse = null;
  var marketingBlocks = [];
  var totalProductsInCollection = 0; // Track total unfiltered products

  var totalProductsInCollectionMode = null;
  // var isMobile = window.innerWidth < 750;
  var isRestoringFromHistory = false; // Flag to prevent duplicate history entries during popstate

  var listingMode = 1;

  // ───────────────────────────────────────────────────────────────────────────
  // Algolia Insights: click/view tracking state
  //
  // Collection pages have no counterpart to search's per-tab "last impression
  // queryID" map because there is only ever one result set here, but the same
  // one-view-per-queryID guard is still needed: performSearch() re-renders on
  // every filter/sort/page change, and the IntersectionObserver below would
  // otherwise re-report already-counted cards whenever the grid is rebuilt.
  // ───────────────────────────────────────────────────────────────────────────
  var lastViewedQueryID = null;
  var cardViewObserver = null;
  var optionHintObserver = null;
  var cardEnhanceObserver = null;
  var cardEnhanceTimer = null;

  function readPlpConfig() {
    var plpConfig = window.PLPConfig;
    if (!plpConfig) {
      return;
    }
    listingMode = plpConfig.listingMode === 'true' ? 0 : 1;
  }

  readPlpConfig();

  // ───────────────────────────────────────────────────────────────────────────
  // Back-navigation result cache
  //
  // On a PDP -> PLP back-navigation without bfcache, the browser does a full
  // fresh document load, so every bit of JS state — including the just-fetched
  // Algolia response — is gone and would normally be re-fetched from scratch.
  // Since that's asking Algolia for data it already had seconds ago, cache the
  // last response per-URL and reuse it instead of re-querying. This also
  // avoids a subtler bug: a fresh query can return products in a slightly
  // different order/count than what was on screen when the user left (stock,
  // price, or ranking changes), which shifts the grid under a saved scroll
  // offset and lands a row off — reusing the frozen response sidesteps that.
  // ───────────────────────────────────────────────────────────────────────────
  var PLP_CACHE_MAX_AGE_MS = 10 * 60 * 1000;

  // Keyed by listing mode as well as URL: the parent/child mode comes from a
  // metafield rather than the URL, and the two modes return different results
  // for the same address. Without it, flipping the mode would serve the other
  // mode's cached hits until the entry expired.
  function getPlpCacheKey() {
    return 'plpCache:' + listingMode + ':' + window.location.pathname + window.location.search;
  }

  function savePlpCache(response, totalCount) {
    try {
      sessionStorage.setItem(
        getPlpCacheKey(),
        JSON.stringify({ response: response, totalProductsInCollection: totalCount, savedAt: Date.now() }),
      );
    } catch {
      // Quota exceeded or unavailable — caching is a pure optimization, safe to skip.
    }
  }

  function loadPlpCache() {
    try {
      var raw = sessionStorage.getItem(getPlpCacheKey());
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || !cached.response) return null;
      if (Date.now() - cached.savedAt > PLP_CACHE_MAX_AGE_MS) return null;
      return cached;
    } catch {
      return null;
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Resolved option-hint cache (parent mode)
  //
  // Option hints ("2 Colors • 4 Sizes") are fetched per-product after render,
  // so a card's height grows once its hint arrives. On a back-navigation the
  // saved scroll offset was measured against hints already resolved, but a
  // fresh render starts with them all unresolved — the shorter cards shift
  // every row, and the restored position lands off by around a row. Caching
  // each resolved value lets those cards render at their final height
  // immediately, so the restored layout matches the one that was measured.
  // Products with no hint cache an empty string and stay collapsed, exactly
  // as they render today.
  // ───────────────────────────────────────────────────────────────────────────
  var PLP_HINT_CACHE_KEY = 'plpOptionHints';
  var optionHintCache = null;

  function getOptionHintCache() {
    if (optionHintCache) return optionHintCache;
    try {
      optionHintCache = JSON.parse(sessionStorage.getItem(PLP_HINT_CACHE_KEY)) || {};
    } catch {
      optionHintCache = {};
    }
    return optionHintCache;
  }

  function saveOptionHint(handle, text) {
    if (!handle) return;
    var cache = getOptionHintCache();
    if (cache[handle] === text) return;
    cache[handle] = text;
    try {
      sessionStorage.setItem(PLP_HINT_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // Quota exceeded or unavailable — hints simply resolve as they do normally.
    }
  }

  // Signals that the grid is at its final DOM/height for this render pass.
  // Sets a flag as well as dispatching, because this script is deferred and so
  // runs (and can finish rendering from cache) before DOMContentLoaded — any
  // listener attached later would otherwise miss the event entirely.
  function signalCollectionReady() {
    window.__algoliaCollectionReady = true;
    document.dispatchEvent(new CustomEvent('algolia:collectionReady'));
  }

  // Placeholder image (same as theme default)
  var PLACEHOLDER_IMAGE = (function () {
    var src = (document.currentScript && document.currentScript.src) || '';
    return src.replace(/algolia-collection\.js.*$/, 'default-magnolia.jpg');
  })();

  // ───────────────────────────────────────────────────────────────────────────
  // Utilities
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Safely query for an element by ID, scoped to Algolia container if possible.
   * Supports both prefixed (algolia-*) and non-prefixed IDs.
   * This prevents conflicts with native Shopify elements with the same ID.
   *
   * @param {string} id - The element ID to search for (with or without 'algolia-' prefix)
   * @param {boolean} requireAlgoliaScope - If true, only returns elements within Algolia container
   * @returns {HTMLElement|null}
   */

  /**
   * Ensure Algolia container is visible and Shopify container is hidden
   * Critical to prevent native Shopify code from interfering with Algolia display
   * Call this before and after any operations that might trigger theme JavaScript
   */
  function ensureAlgoliaContainerVisible() {
    var shopifyContainer = document.getElementById('shopify-collection-grid');
    var algoliaContainer = document.getElementById('algolia-collection-container');

    if (shopifyContainer) {
      shopifyContainer.style.display = 'none';
      shopifyContainer.style.visibility = 'hidden';
    }

    if (algoliaContainer) {
      algoliaContainer.style.display = 'block';
      algoliaContainer.style.visibility = 'visible';
    }
  }

  /**
   * Setup MutationObserver to protect container visibility
   * Prevents external theme code from hiding Algolia or showing native Shopify
   * This is a defensive measure against race conditions and async theme code
   */
  function setupContainerVisibilityProtection() {
    if (!window.MutationObserver) return; // Skip if not supported

    var shopifyContainer = document.getElementById('shopify-collection-grid');
    var algoliaContainer = document.getElementById('algolia-collection-container');

    if (!algoliaContainer) return;

    // Observe style changes on Algolia container
    var algoliaObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          var currentDisplay = algoliaContainer.style.display;
          var currentVisibility = algoliaContainer.style.visibility;

          // If something tried to hide Algolia, show it again
          if (currentDisplay === 'none' || currentVisibility === 'hidden') {
            console.warn('[Algolia] External code tried to hide Algolia container - re-enforcing visibility');
            ensureAlgoliaContainerVisible();
          }
        }
      });
    });

    algoliaObserver.observe(algoliaContainer, {
      attributes: true,
      attributeFilter: ['style'],
    });

    // Observe style changes on Shopify container
    if (shopifyContainer) {
      var shopifyObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            var currentDisplay = shopifyContainer.style.display;
            var currentVisibility = shopifyContainer.style.visibility;

            // If something tried to show Shopify container, hide it again
            if (currentDisplay !== 'none' || currentVisibility !== 'hidden') {
              console.warn('[Algolia] External code tried to show Shopify container - re-hiding it');
              ensureAlgoliaContainerVisible();
            }
          }
        });
      });

      shopifyObserver.observe(shopifyContainer, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }
  }

  function getCollectionData() {
    var el = document.getElementById('collection-data');
    if (!el) return null;
    return {
      handle: el.getAttribute('data-collection-handle'),
      id: el.getAttribute('data-collection-id'),
      hitsPerPage: parseInt(el.getAttribute('data-products-per-page') || '48', 10),
    };
  }

  /**
   * Shopify template name for the page this widget is running on, used as
   * clickData.pageType. The AEP pixel maps it to both
   * webPageDataDetails.pageType and .moduleType on the linkClicked event, so
   * without it every facet/sort/pagination click would report an empty
   * pageType while the page view event reports "collection".
   * @returns {string}
   */
  function getPageType() {
    var el = document.getElementById('collection-data');
    return (el && el.getAttribute('data-page-type')) || 'collection';
  }

  function formatPrice(amount) {
    if (typeof amount === 'object' && amount.amount) {
      amount = amount.amount;
    }
    var dollars = amount.toFixed(2);
    return '$' + dollars;
  }

  function renderSkeletonGrid() {
    var container = document.getElementById('algolia-collection-grid');
    if (!container) return;

    // CRITICAL: Preserve ProductCount visibility during loading
    // Ensure ProductCount elements stay visible even when skeleton grid is shown
    var algoliaContainer = document.getElementById('algolia-collection-container');
    if (algoliaContainer) {
      var productCountDesktop = algoliaContainer.querySelector(
        '#AlgoliaProductCountDesktop, #algolia-ProductCountDesktop',
      );
      var productCountMobile = algoliaContainer.querySelector(
        '#AlgoliaProductCountMobile, #algolia-ProductCountMobile',
      );

      if (productCountDesktop) {
        productCountDesktop.style.display = 'inline';
        productCountDesktop.style.visibility = 'visible';
      }
      if (productCountMobile) {
        productCountMobile.style.display = 'inline';
        productCountMobile.style.visibility = 'visible';
      }
    }
    var count = Math.min(hitsPerPage || 12, 12);
    var html = '';
    for (var i = 0; i < count; i++) {
      html += `
        <li class="grid__item algolia-collection-skeleton-card" aria-hidden="true">
          <div class="card-wrapper">
            <div class="card card--standard card--media">
              <div class="card__inner ratio" style="--ratio-percent: 100%;">
                <span class="algolia-skeleton-bar algolia-skeleton-image"></span>
              </div>
              <div class="algolia-collection-skeleton-body">
                <span class="algolia-skeleton-bar algolia-skeleton-title"></span>
                <span class="algolia-skeleton-bar algolia-skeleton-price"></span>
              </div>
            </div>
          </div>
        </li>
      `;
    }
    container.innerHTML = html;
  }

  function escapeHtml(text) {
    // Handle null, undefined, or non-string values
    if (text == null) return '';
    if (typeof text !== 'string') text = String(text);

    var map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, function (m) {
      return map[m];
    });
  }

  var productDataCache = {};

  function pluralize(word) {
    var w = typeof word === 'string' ? word : String(word || '');
    if (!w || w.slice(-1) === 's') return w;
    if (w.slice(-2) === 'sh' || w.slice(-2) === 'ch' || w.slice(-1) === 'x' || w.slice(-1) === 'z') return w + 'es';
    return w + 's';
  }

  function fetchProductData(handle) {
    if (productDataCache[handle] !== undefined) {
      return Promise.resolve(productDataCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (product) {
        var data = product && product.product ? product.product : product;
        productDataCache[handle] = data;
        return data;
      })
      .catch(function () {
        productDataCache[handle] = null;
        return null;
      });
  }

  function fetchProductOptions(handle) {
    return fetchProductData(handle).then(function (data) {
      var rawOptions = data && Array.isArray(data.options) ? data.options : [];
      var variants = data && Array.isArray(data.variants) ? data.variants : [];

      return rawOptions.map(function (opt, i) {
        var name, count;
        if (opt !== null && typeof opt === 'object') {
          name = typeof opt.name === 'string' ? opt.name : String(opt.name || '');
          count = Array.isArray(opt.values) ? opt.values.length : 0;
        } else {
          name = typeof opt === 'string' ? opt : String(opt || '');
          count = 0;
        }
        if (count === 0 && variants.length > 0) {
          var key = 'option' + (i + 1);
          var seen = {};
          variants.forEach(function (v) {
            if (v[key]) seen[v[key]] = true;
          });
          count = Object.keys(seen).length;
        }
        return { name: name, count: count };
      });
    });
  }

  function enhanceOptionHintForCard(card, handle) {
    fetchProductOptions(handle).then(function (opts) {
      var hintEl = card.querySelector('[data-option-hint]');
      if (!hintEl) return;
      var multiOpts = opts.filter(function (o) {
        return o.count > 1;
      });
      if (multiOpts.length === 0) {
        saveOptionHint(handle, '');
        hintEl.style.display = 'none';
        scheduleScrollReapply();
        return;
      }
      var parts = multiOpts.map(function (o) {
        return o.count + ' ' + pluralize(o.name);
      });
      var text = parts.join(' • ');
      saveOptionHint(handle, text);
      hintEl.textContent = text;
      hintEl.style.display = '';
      scheduleScrollReapply();
    });
  }

  // Hints resolving after a scroll restore change row heights above the
  // viewport, which shifts the restored position. Re-apply the saved offset
  // once the burst of resolutions settles. Only armed briefly after a restore,
  // and scroll-restoration.js ignores the call once the user starts scrolling.
  var scrollReapplyArmed = false;
  var scrollReapplyTimer = null;
  var scrollReapplyArmTimer = null;

  function armScrollReapply() {
    scrollReapplyArmed = true;
    clearTimeout(scrollReapplyArmTimer);
    scrollReapplyArmTimer = setTimeout(function () {
      scrollReapplyArmed = false;
      scrollReapplyArmTimer = null;
    }, 3000);
  }

  function scheduleScrollReapply() {
    if (!scrollReapplyArmed || typeof window.__plpReapplyScroll !== 'function') return;
    clearTimeout(scrollReapplyTimer);
    scrollReapplyTimer = setTimeout(function () {
      window.__plpReapplyScroll();
    }, 120);
  }

  function lazyEnhanceOptionHints(container) {
    if (optionHintObserver) optionHintObserver.disconnect();
    optionHintObserver = null;
    // Cards rendered from the hint cache are already at their final value.
    var cards = Array.prototype.filter.call(container.querySelectorAll('[data-product-handle]'), function (card) {
      return !card.querySelector('[data-hint-resolved]');
    });
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach(function (card) {
        enhanceOptionHintForCard(card, card.getAttribute('data-product-handle'));
      });
      return;
    }
    optionHintObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          optionHintObserver.unobserve(entry.target);
          enhanceOptionHintForCard(entry.target, entry.target.getAttribute('data-product-handle'));
        });
      },
      { rootMargin: '400px 0px' },
    );
    cards.forEach(function (card) {
      optionHintObserver.observe(card);
    });
  }

  function enhanceCardsAfterGlo(container) {
    clearTimeout(cardEnhanceTimer);
    if (document.documentElement.classList.contains('webview')) {
      cardEnhanceTimer = setTimeout(function () {
        lazyEnhanceCards(container);
      }, 500);
    } else {
      lazyEnhanceCards(container);
    }
  }

  function lazyEnhanceCards(container) {
    if (cardEnhanceObserver) cardEnhanceObserver.disconnect();
    cardEnhanceObserver = null;
    injectCarouselStyles();
    var cards = container.querySelectorAll('[data-product-handle]');
    if (!('IntersectionObserver' in window)) {
      cards.forEach(enhanceCard);
      return;
    }
    cardEnhanceObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          cardEnhanceObserver.unobserve(entry.target);
          enhanceCard(entry.target);
        });
      },
      { rootMargin: '400px 0px' },
    );
    cards.forEach(function (card) {
      cardEnhanceObserver.observe(card);
    });
  }

  function enhanceCard(card) {
    var handle = card.getAttribute('data-product-handle');
    if (!handle) return;
    //enhanceOptionHintsForCard(card, handle);
    enhanceImageCarouselForCard(card, handle);
  }

  function enhanceImageCarouselForCard(card, handle) {
    var mediaEl = card.querySelector('.hit-image');
    if (!mediaEl || mediaEl.hasAttribute('data-carousel-ready')) return;

    fetchProductData(handle).then(function (data) {
      // Another render pass may have already handled this exact element.
      if (mediaEl.hasAttribute('data-carousel-ready')) return;
      mediaEl.setAttribute('data-carousel-ready', '1');

      if (listingMode !== 0 && mediaEl.getAttribute('data-image-source') !== 'algolia') {
        var featuredImage = getFeaturedImageSrc(data);
        var imageEl = mediaEl.querySelector('img');
        if (featuredImage && imageEl) {
          setResponsiveImageSource(imageEl, featuredImage);
          mediaEl.setAttribute('data-image-source', 'shopify');
        }
      }

      var images = getProductImageSources(data).slice(0, CAROUSEL_MAX_IMAGES);
      if (images.length < 2) return;

      // Warm the browser cache so switching images feels instant.
      images.forEach(function (src) {
        var preload = new Image();
        preload.src = src;
      });

      mediaEl.setAttribute('data-carousel-images', JSON.stringify(images));
      mediaEl.setAttribute('data-carousel-index', '0');

      var arrowsHtml =
        '<button type="button" class="hit-carousel__arrow hit-carousel__arrow--prev" aria-label="Previous image">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
        '</button>' +
        '<button type="button" class="hit-carousel__arrow hit-carousel__arrow--next" aria-label="Next image">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
        '</button>';

      var dotsHtml =
        '<div class="hit-carousel__dots">' +
        images
          .map(function (_, i) {
            return (
              '<button type="button" class="hit-carousel__dot' +
              (i === 0 ? ' is-active' : '') +
              '" data-carousel-dot="' +
              i +
              '" aria-label="Show image ' +
              (i + 1) +
              '"></button>'
            );
          })
          .join('') +
        '</div>';

      mediaEl.insertAdjacentHTML('beforeend', arrowsHtml + dotsHtml);
    });
  }

  var CAROUSEL_MAX_IMAGES = 6;

  function injectCarouselStyles() {
    if (document.getElementById('algolia-carousel-styles')) return;
    var style = document.createElement('style');
    style.id = 'algolia-carousel-styles';
    style.textContent =
      '.hit-image{position:relative;}' +
      '.hit-image[data-carousel-images]{touch-action:pan-y;}' +
      '.hit-carousel__arrow{position:absolute !important;top:50% !important;left:auto !important;transform:translateY(-50%);' +
      'width:32px !important;height:32px !important;border-radius:50%;background:#fff;border:none;' +
      'display:flex !important;align-items:center;justify-content:center;cursor:pointer;' +
      'box-shadow:0 1px 4px rgba(0,0,0,.2);z-index:2;padding:0;color:#333;' +
      'opacity:0;pointer-events:none;transition:opacity .15s ease;}' +
      '.hit-carousel__arrow--prev{left:8px;}' +
      '.hit-carousel__arrow--next{right:8px;}' +
      '.hit-carousel__arrow svg{width:16px;height:16px;}' +
      '.hit-carousel__dots{position:absolute !important;left:0 !important;top:auto !important;height:auto !important;right:0 !important;bottom:45px;' +
      'display:flex !important;justify-content:center;gap:6px;z-index:1;' +
      'opacity:0;pointer-events:none;transition:opacity .15s ease;}' +
      '.hit-carousel__dot{width:6px;height:6px;border-radius:50%;' +
      'background:rgba(255,255,255,.65);border:none;padding:0;cursor:pointer;}' +
      '.hit-carousel__dot.is-active{background:#fff;box-shadow:0 0 0 1px rgba(0,0,0,.25);}' +
      '.hit-wrapper:hover .hit-carousel__arrow,' +
      '.hit-wrapper:focus-within .hit-carousel__arrow,' +
      '.hit-wrapper:hover .hit-carousel__dots,' +
      '.hit-wrapper:focus-within .hit-carousel__dots{opacity:1;pointer-events:auto;}' +
      /* Mobile: dots only, always visible; no arrows — swipe replaces them. */
      '@media (max-width:749px){' +
      '.hit-carousel__arrow{display:none!important;}' +
      '.hit-carousel__dots{opacity:1!important;pointer-events:none;bottom:12px;}' +
      '}';
    document.head.appendChild(style);
  }

  function normalizeImageSrc(im) {
    if (typeof im === 'string') return im;
    return (im && im.src) || null;
  }

  function getFeaturedImageSrc(product) {
    return normalizeImageSrc(product && product.featured_image);
  }

  function getHitImageSrc(hit) {
    return (
      normalizeImageSrc(hit && hit.product_image) ||
      normalizeImageSrc(hit && hit.image) ||
      normalizeImageSrc(hit && hit.images && hit.images[0]) ||
      PLACEHOLDER_IMAGE
    );
  }

  function getProductImageSources(product) {
    var featuredImage = getFeaturedImageSrc(product);
    var images = (product && Array.isArray(product.images) ? product.images : [])
      .map(normalizeImageSrc)
      .filter(Boolean);
    if (featuredImage) {
      images = [featuredImage].concat(
        images.filter(function (image) {
          return image !== featuredImage;
        }),
      );
    }
    return images;
  }

  var RESPONSIVE_IMAGE_WIDTHS = [360, 533, 720, 940, 1066, 1440, 2000];
  var PRODUCT_CARD_IMAGE_SIZES =
    '(min-width: 1200px) calc((100vw - 16rem) / 5), (min-width: 750px) calc((100vw - 12rem) / 3), calc((100vw - 3.5rem) / 2)';

  function withImageWidth(src, width) {
    if (!src || src === PLACEHOLDER_IMAGE) return src;
    try {
      var url = new URL(src, window.location.origin);
      var isShopifyCdn = url.hostname.indexOf('shopify') !== -1 || url.pathname.indexOf('/cdn/') !== -1;
      if (!isShopifyCdn) return src;
      url.searchParams.set('width', width);
      return url.toString();
    } catch {
      return src;
    }
  }

  function setResponsiveImageSource(img, src) {
    if (!img || !src) return;
    var responsiveSrc = withImageWidth(src, 533);
    var srcset = RESPONSIVE_IMAGE_WIDTHS.map(function (width) {
      return withImageWidth(src, width) + ' ' + width + 'w';
    }).join(', ');
    img.src = responsiveSrc;
    img.srcset = srcset;
    img.sizes = PRODUCT_CARD_IMAGE_SIZES;
  }

  function getResponsiveImageMarkup(src, alt) {
    var responsiveSrc = withImageWidth(src, 533);
    var srcset = RESPONSIVE_IMAGE_WIDTHS.map(function (width) {
      return withImageWidth(src, width) + ' ' + width + 'w';
    }).join(', ');
    return (
      '<img srcset="' +
      escapeHtml(srcset) +
      '" src="' +
      escapeHtml(responsiveSrc) +
      '" sizes="' +
      PRODUCT_CARD_IMAGE_SIZES +
      '" alt="' +
      alt +
      '" class="motion-reduce" loading="lazy" width="533" height="533">'
    );
  }

  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('.hit-carousel__arrow');
    var dot = e.target.closest('.hit-carousel__dot');
    if (!arrow && !dot) return;
    e.preventDefault();
    e.stopPropagation();
    var mediaEl = (arrow || dot).closest('.hit-image');
    if (!mediaEl) return;
    var current = parseInt(mediaEl.getAttribute('data-carousel-index') || '0', 10);
    if (arrow) {
      var dir = arrow.classList.contains('hit-carousel__arrow--next') ? 1 : -1;
      setCarouselIndex(mediaEl, current + dir);
    } else {
      setCarouselIndex(mediaEl, parseInt(dot.getAttribute('data-carousel-dot'), 10));
    }
  });

  function setCarouselIndex(mediaEl, index) {
    var raw = mediaEl.getAttribute('data-carousel-images');
    if (!raw) return;
    var images = JSON.parse(raw);
    var next = ((index % images.length) + images.length) % images.length;
    mediaEl.setAttribute('data-carousel-index', String(next));
    var img = mediaEl.querySelector('img');
    if (img) setResponsiveImageSource(img, images[next]);
    mediaEl.querySelectorAll('.hit-carousel__dot').forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === next);
    });
  }

  var carouselSwipe = null;
  var CAROUSEL_SWIPE_THRESHOLD = 30;

  document.addEventListener(
    'touchstart',
    function (e) {
      var mediaEl = e.target.closest && e.target.closest('.hit-image[data-carousel-images]');
      if (!mediaEl) return;
      var t = e.touches[0];
      carouselSwipe = { mediaEl: mediaEl, startX: t.clientX, startY: t.clientY, horizontal: false };
    },
    { passive: true },
  );

  document.addEventListener(
    'touchmove',
    function (e) {
      if (!carouselSwipe) return;
      var t = e.touches[0];
      var dx = t.clientX - carouselSwipe.startX;
      var dy = t.clientY - carouselSwipe.startY;
      // Once a drag reveals itself as horizontal, remember that — avoids
      // flip-flopping mid-gesture and lets a mostly-vertical page scroll
      // pass through untouched.
      if (!carouselSwipe.horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        carouselSwipe.horizontal = true;
      }
    },
    { passive: true },
  );

  document.addEventListener('touchcancel', function () {
    carouselSwipe = null;
  });

  document.addEventListener('touchend', function (e) {
    if (!carouselSwipe) return;
    var swipe = carouselSwipe;
    carouselSwipe = null;
    if (!swipe.horizontal) return;
    var t = e.changedTouches[0];
    var dx = t.clientX - swipe.startX;
    if (Math.abs(dx) < CAROUSEL_SWIPE_THRESHOLD) return;
    var current = parseInt(swipe.mediaEl.getAttribute('data-carousel-index') || '0', 10);
    setCarouselIndex(swipe.mediaEl, current + (dx < 0 ? 1 : -1));
  });

  document.addEventListener('DOMContentLoaded', function () {
    const images = document.querySelectorAll('img');

    images.forEach((img) => {
      img.addEventListener('mouseenter', function () {
        // Temporarily store the title value and empty it
        this.dataset.storedTitle = this.title;
        this.title = '';
      });

      img.addEventListener('mouseleave', function () {
        // Restore the title value when mouse moves away
        if (this.dataset.storedTitle) {
          this.title = this.dataset.storedTitle;
        }
      });
    });
  });

  // ───────────────────────────────────────────────────────────────────────────
  // Algolia Search with Disjunctive Faceting
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Build Algolia facetFilters array for native disjunctive faceting
   * Format: [["facet1:val1", "facet1:val2"], ["facet2:val3"]]
   * - Inner arrays are OR (disjunctive) - e.g., Nike OR Adidas
   * - Outer array is AND (conjunctive) - e.g., (Nike OR Adidas) AND Red
   *
   * Example output:
   * [["vendor:Nike", "vendor:Adidas"], ["color:Red"]]
   * Means: (vendor=Nike OR vendor=Adidas) AND (color=Red)
   */
  function buildFacetFilters() {
    var facetFilters = [];

    // Add each facet's values as an OR group
    Object.keys(currentFilters).forEach(function (facet) {
      var values = currentFilters[facet];
      if (values && values.length > 0) {
        // Create OR group for this facet: ["vendor:Nike", "vendor:Adidas"]
        var facetGroup = values.map(function (val) {
          return facet + ':' + val;
        });
        facetFilters.push(facetGroup);
      }
    });

    return facetFilters;
  }

  /**
   * Build base filters string (non-facet filters)
   * Only includes collection filter
   */
  function buildFiltersString() {
    // Base filter: restrict to current collection
    return 'collections:' + collectionHandle;
  }

  // Unfiltered count for the "X of Y" label; batched with the main query.
  function totalCountQuery() {
    return {
      indexName: config.indices.products,
      params: {
        filters: buildFiltersString(),
        hitsPerPage: 0,
        distinct: listingMode,
        facetingAfterDistinct: listingMode,
      },
    };
  }

  function setTotalProductsInCollection(result) {
    totalProductsInCollection = result ? result.nbHits : 0;
    totalProductsInCollectionMode = listingMode;
  }

  function getIndexName() {
    var baseIndex = config.indices.products;

    // Use replica index for sorting (matching search page)
    if (currentSort === 'most_relevant') return config.indices.productsNewest;
    if (currentSort === 'best_selling') return config.indices.productsBestSelling;
    if (currentSort === 'title_asc') return config.indices.productsAsc;
    if (currentSort === 'title_desc') return config.indices.productsDesc;
    if (currentSort === 'price_asc') return config.indices.productsPriceAsc;
    if (currentSort === 'price_desc') return config.indices.productsPriceDesc;
    if (currentSort === 'created_asc') return config.indices.productsOldest;
    if (currentSort === 'created_desc') return config.indices.productsNewest;

    return baseIndex; // default relevance
  }

  /**
   * Load products with disjunctive faceting
   * Same pattern as algolia-instantsearch.js
   * Runs multiple queries to keep facet counts accurate when filtering
   */
  function loadWithDisjunctiveFacets(includeTotal) {
    var indexName = getIndexName();
    var baseFilters = buildFiltersString(); // "collections:xyz"
    var facetFilters = buildFacetFilters(); // [["vendor:Nike", "vendor:Adidas"], ["color:Red"]]

    var requestedFacets = [
      'meta.filters.filter_product_category.fields.name',
      'meta.filters.filter_product_type.fields.name',
      'price_range',
      'meta.netsuite.color',
    ];

    // Main query parameters
    var mainParams = {
      query: currentQuery || '',
      filters: baseFilters,
      facets: requestedFacets,
      maxValuesPerFacet: 100,
      distinct: listingMode,
      facetingAfterDistinct: listingMode,
      hitsPerPage: hitsPerPage,
      page: currentPage,
    };

    if (facetFilters.length > 0) {
      mainParams.facetFilters = facetFilters;
    }

    // Start with main query
    var queries = [{ indexName: indexName, params: mainParams }];

    // Find which facets currently have filters applied
    var filteredFacets = Object.keys(currentFilters).filter(function (facet) {
      return currentFilters[facet] && currentFilters[facet].length > 0;
    });

    // Add one query per filtered facet to get accurate counts
    filteredFacets.forEach(function (excludeFacet) {
      // Build facetFilters WITHOUT this specific facet
      var otherFacetFilters = [];
      Object.keys(currentFilters).forEach(function (facet) {
        if (facet === excludeFacet) return; // Skip this one
        var values = currentFilters[facet];
        if (values && values.length > 0) {
          var facetGroup = values.map(function (val) {
            return facet + ':' + val;
          });
          otherFacetFilters.push(facetGroup);
        }
      });

      var facetParams = {
        query: currentQuery || '',
        filters: baseFilters,
        facets: [excludeFacet], // Only get this facet's counts
        maxValuesPerFacet: 100,
        distinct: listingMode,
        facetingAfterDistinct: listingMode,
        hitsPerPage: 0, // Don't need products
        page: 0,
      };

      if (otherFacetFilters.length > 0) {
        facetParams.facetFilters = otherFacetFilters;
      }

      queries.push({ indexName: indexName, params: facetParams });
    });

    if (includeTotal) queries.push(totalCountQuery());

    // Execute all queries in parallel
    return searchClient.search(queries).then(function (response) {
      var mainResult = response.results[0];
      // Merge facet counts from disjunctive queries back into main result
      filteredFacets.forEach(function (facet, index) {
        var facetQueryResult = response.results[index + 1];
        if (facetQueryResult && facetQueryResult.facets && facetQueryResult.facets[facet]) {
          mainResult.facets = mainResult.facets || {};
          mainResult.facets[facet] = facetQueryResult.facets[facet];
        }
      });

      if (includeTotal) setTotalProductsInCollection(response.results[queries.length - 1]);

      return mainResult;
    });
  }

  function performSearch() {
    // CRITICAL: Ensure Algolia container is visible BEFORE search
    // Prevents native Shopify theme code from showing during async search
    ensureAlgoliaContainerVisible();

    renderSkeletonGrid();

    var needsTotal = totalProductsInCollectionMode !== listingMode;
    // With no filters or search text the main query's nbHits already is the total.
    var mainIsUnfiltered = buildFacetFilters().length === 0 && !currentQuery;

    return loadWithDisjunctiveFacets(needsTotal && !mainIsUnfiltered)
      .then(function (result) {
        if (needsTotal && mainIsUnfiltered) setTotalProductsInCollection(result);
        lastResponse = result;

        // render() updates the URL via updateUrlState()'s pushState, so cache
        // under the *settled* URL after render, not the pre-render one —
        // otherwise the save key won't match what's in the address bar (and
        // thus what a later back-navigation will look the cache up under).
        render();
        recordAlgoliaSearch();
        savePlpCache(lastResponse, totalProductsInCollection);

        // No search-results event here. Collection pages must not fire
        // `search-event` / `internalSearchTerm` — that event has no counterpart
        // in the collection context, and the page view event published by
        // theme.liquid on load is the only page-level event a collection needs
        // . Active filter terms and pagination depth are read from
        // the URL on that page view event.

        // CRITICAL: Re-enforce container visibility AFTER render
        // Prevents theme JavaScript from interfering after DOM updates
        ensureAlgoliaContainerVisible();
      })
      .catch(function (err) {
        console.warn(err);
        renderApiErrorState();
      });
  }

  function renderApiErrorState() {
    var container = document.getElementById('algolia-collection-grid');
    if (!container) return;

    var statsContainer = document.getElementById('algolia-collection-stats');
    if (statsContainer) statsContainer.textContent = '';

    var resultsHeader = document.querySelector('.algolia-results-header');
    if (resultsHeader) resultsHeader.style.display = 'none';

    var facetsWrapper = document.getElementById('algolia-collection-facets-wrapper');
    if (facetsWrapper) facetsWrapper.style.display = 'none';

    var collectionHero = document.querySelector('.collection-hero');
    if (collectionHero) collectionHero.style.display = 'none';

    var collectionNav = document.querySelector('.collection-nav');
    var collectionNavWrapper = collectionNav ? collectionNav.closest('.shopify-section') : null;
    if (collectionNavWrapper) {
      collectionNavWrapper.style.display = 'none';
    } else if (collectionNav) {
      collectionNav.style.display = 'none';
    }

    var secondaryNav = document.querySelector('secondary-nav.secondary-nav');
    var secondaryNavWrapper = secondaryNav ? secondaryNav.closest('.color-scheme-linen') : null;
    if (secondaryNavWrapper) secondaryNavWrapper.style.display = 'none';

    var headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) headerWrapper.style.borderBottom = 'none';

    var pagination = document.getElementById('algolia-collection-pagination');
    if (pagination) pagination.style.display = 'none';

    var gridSection = document.getElementById('algolia-collection-container');
    var sectionWrapper = gridSection ? gridSection.closest('.shopify-section') : null;
    if (sectionWrapper) {
      var sibling = sectionWrapper.nextElementSibling;
      while (sibling) {
        sibling.style.display = 'none';
        sibling = sibling.nextElementSibling;
      }
    }

    var newsletterSection = document.querySelector('.newsletter');
    var newsletterWrapper = newsletterSection ? newsletterSection.closest('.shopify-section') : null;
    if (newsletterWrapper) newsletterWrapper.style.display = 'none';

    var continueShoppingUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    container.innerHTML = `
      <div class="collection-empty collection-api-error">
        <p class="body-text">We're having trouble loading products right now. Please try again.</p>
        <button type="button" class="button" id="algolia-api-error-retry">Try Again</button>
        <a href="${escapeHtml(continueShoppingUrl)}" class="link" style="margin-top: 1rem;">Continue Shopping</a>
      </div>
    `;

    var retryBtn = container.querySelector('#algolia-api-error-retry');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        window.location.reload();
      });
    }

    signalCollectionReady();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Rendering
  // ───────────────────────────────────────────────────────────────────────────
  function render() {
    if (!lastResponse) return;

    // Ensure Algolia container is visible before rendering
    ensureAlgoliaContainerVisible();

    try {
      renderStats();
      renderProducts();
      renderFacets();
      renderPagination();
      renderProductCountBottom();
      renderMobileFilterButton();
      updateSortDropdown(); // Ensure sort dropdown reflects current sort state
      updateUrlState();

      // Final enforcement after all renders complete
      // This prevents theme code from interfering after DOM manipulation
      ensureAlgoliaContainerVisible();
    } catch (err) {
      // Show error to user
      var container = document.getElementById('algolia-collection-grid');
      if (container) {
        container.innerHTML =
          '<li style="grid-column: 1/-1; padding: 2rem; text-align: center;">' +
          '<h2>Error rendering products</h2>' +
          '<p>Please refresh the page or contact support.</p>' +
          '<p style="color: #999; font-size: 0.875rem;">Error: ' +
          escapeHtml(err.message) +
          '</p>' +
          '</li>';
      }
    }

    // Signal that the grid reflects its final DOM/height for this render pass,
    // so scroll-restoration.js can wait for this instead of racing the async search.
    signalCollectionReady();
  }

  function renderStats() {
    // Get Algolia container to scope all queries
    var algoliaContainer = document.getElementById('algolia-collection-container');
    if (!algoliaContainer) return;

    var container = document.getElementById('algolia-collection-stats');
    var desktopContainer = document.getElementById('algolia-collection-stats-desktop');
    var mobileContainer = document.getElementById('algolia-collection-stats-mobile');

    // Query for ProductCount elements ONLY within Algolia container to avoid duplicates
    // Support multiple ID formats: AlgoliaProductCount* (primary), algolia-ProductCount*, ProductCount*
    var productCountMobile = algoliaContainer.querySelector('#AlgoliaProductCountMobile, #algolia-ProductCountMobile');
    var productCountDesktop = algoliaContainer.querySelector(
      '#AlgoliaProductCountDesktop, #algolia-ProductCountDesktop',
    );

    // Defensive check: ensure lastResponse exists before accessing nbHits
    // CRITICAL: Check this BEFORE modifying elements to prevent showing empty text
    if (!lastResponse || typeof lastResponse.nbHits === 'undefined') {
      console.warn('Cannot render stats: lastResponse is invalid or missing nbHits');
      return;
    }

    var filteredCount = lastResponse.nbHits;
    var text = '';

    // Determine if filters are active (excluding page/sort which don't affect count display)
    var filtersActive = hasActiveFilters() || currentQuery;

    // Calculate text FIRST before enforcing visibility
    if (filtersActive && totalProductsInCollection > 0 && filteredCount !== totalProductsInCollection) {
      // Show "X of Y products" when filters are applied
      text =
        filteredCount + ' of ' + totalProductsInCollection + ' Item' + (totalProductsInCollection !== 1 ? 's' : '');
    } else {
      // Show "X products" when no filters or all products shown
      text = filteredCount + ' Item' + (filteredCount !== 1 ? 's' : '');
    }

    // CRITICAL: Update child containers FIRST before parent containers
    // This prevents textContent from removing child nodes

    // Update all stats containers with forced reflow to ensure DOM updates
    if (container) {
      container.textContent = text;
      // Force reflow to ensure immediate visual update
      void container.offsetHeight;
    }

    // Update desktop stats container (child of ProductCountDesktop)
    // CRITICAL: Update this BEFORE ProductCountDesktop to avoid removing it via textContent
    if (desktopContainer) {
      desktopContainer.textContent = text;
      desktopContainer.style.display = 'inline';
      desktopContainer.style.visibility = 'visible';
      void desktopContainer.offsetHeight;
    }

    // Update mobile stats container (child of ProductCountMobile)
    // CRITICAL: Update this BEFORE ProductCountMobile to avoid removing it via textContent
    if (mobileContainer) {
      mobileContainer.textContent = text;
      mobileContainer.style.display = 'inline';
      mobileContainer.style.visibility = 'visible';
      void mobileContainer.offsetHeight;
    }

    // Update ProductCountDesktop element visibility (parent container)
    // CRITICAL: Only set visibility/display styles, NOT textContent
    // Setting textContent would remove the child algolia-collection-stats-desktop element
    if (productCountDesktop) {
      productCountDesktop.style.display = 'inline';
      productCountDesktop.style.visibility = 'visible';
      // Mark element to prevent overwriting by native Shopify code
      productCountDesktop.setAttribute('data-algolia-updated', 'true');
      void productCountDesktop.offsetHeight;

      // If desktopContainer doesn't exist as a child, update ProductCountDesktop directly
      if (!desktopContainer) {
        productCountDesktop.textContent = text;
      }
    }

    // Update ProductCountMobile element visibility (parent container)
    // CRITICAL: Only set visibility/display styles, NOT textContent
    // Setting textContent would remove the child algolia-collection-stats-mobile element
    if (productCountMobile) {
      productCountMobile.style.display = 'inline';
      productCountMobile.style.visibility = 'visible';
      // Mark element to prevent overwriting by native Shopify code
      productCountMobile.setAttribute('data-algolia-updated', 'true');
      void productCountMobile.offsetHeight;

      // If mobileContainer doesn't exist as a child, update ProductCountMobile directly
      if (!mobileContainer) {
        productCountMobile.textContent = text;
      }
    }
  }

  function renderProducts() {
    var container = document.getElementById('algolia-collection-grid');
    if (!container) return;

    if (lastResponse.hits.length === 0) {
      container.innerHTML = renderEmptyState();
      return;
    }

    var html = '';

    lastResponse.hits.forEach(function (hit, index) {
      // Calculate the position of this product in the overall grid
      var absolutePosition = currentPage * hitsPerPage + index + 1;

      // Check if a marketing block should appear at this position (BEFORE this product)
      var marketingBlock = getMarketingBlockAtPosition(absolutePosition);

      if (marketingBlock) {
        // Determine if this block should be lazy loaded (only positions > 2 use lazy loading)
        var lazyLoad = absolutePosition > 2;

        var blockHtml = renderMarketingBlock(marketingBlock, lazyLoad);
        if (blockHtml) {
          html += blockHtml;
        }
      }

      // Add the product
      html += renderProductCard(hit, index);
    });

    container.innerHTML = html;

    if (typeof window.globoswatch !== 'undefined' && typeof window.globoswatch.init === 'function') {
      window.globoswatch.init();
    }
    enhanceCardsAfterGlo(container);
    observeCardsForViews(container);

    // Trigger MTO.js if present (for swatch replacement)
    if (window.mtoSwatches && typeof window.mtoSwatches.init === 'function') {
      setTimeout(function () {
        window.mtoSwatches.init();
      }, 100);
    }

    // Parent mode: lazily fetch each product's option counts (e.g. "2 Colors • 4 Sizes").
    // If a back-navigation scroll restore is in flight, wait for it to finish first —
    // otherwise hint text landing above the target card grows it and shifts the
    // just-restored scroll position.
    if (listingMode === 1) {
      // Not gated on the scroll restore the way hints are: the label and the
      // resolved price both occupy a single line, so this swap cannot shift a
      // restored position.
      resolveZeroPriceCards(container);

      if (document.documentElement.classList.contains('restoring-scroll')) {
        document.addEventListener(
          'scroll-restoration:done',
          function () {
            armScrollReapply();
            lazyEnhanceOptionHints(container);
          },
          { once: true },
        );
      } else {
        lazyEnhanceOptionHints(container);
      }
    } else if (listingMode === 0) {
      resolveZeroPriceChildCards(container);
    }
  }

  function renderEmptyState() {
    return `
      <div class="collection-empty" style="grid-column: 1 / -1; text-align: center;">
        <h2 class="title title--primary text-body">Sorry, no products match your criteria.<br>Clear or modify your filters and try again.</h2>
        <button class="button button--secondary" id="algolia-clear-filters">
          Clear Filters
        </button>
      </div>
    `;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Badge Helpers (matching search page implementation)
  // ───────────────────────────────────────────────────────────────────────────
  function getBadgeForHit(hit) {
    var configs = window.productBadgeConfigs;
    if (!configs || !configs.length) return null;

    var tags = Array.isArray(hit.tags) ? hit.tags : [];
    var publishedAt = hit.published_at;
    var now = Date.now();

    for (var i = 0; i < configs.length; i++) {
      var badge = configs[i];

      // Tag condition
      if (badge.condition_tag_name) {
        if (tags.indexOf(badge.condition_tag_name) === -1) continue;
      }

      // Inventory cap condition
      if (badge.condition_inventory_cap != null && badge.condition_inventory_cap !== '') {
        var cap = badge.condition_inventory_cap;
        var qty = hit.inventory_quantity != null ? hit.inventory_quantity : null;
        var totalQty = hit.variants_inventory_count != null ? hit.variants_inventory_count : null;
        var isOversellAllowed = hit.inventory_policy === 'continue';
        if (qty == null) continue;
        if (isOversellAllowed) continue;
        if (badge.condition_inventory_cap_for_all_variants) {
          if (totalQty == null || totalQty > cap || (cap > 0 && totalQty <= 0)) continue;
        } else {
          if (cap > 0 && qty <= 0) continue;
          if (qty > cap) continue;
        }
      }

      // Publish date range condition
      if (badge.condition_publish_date_range != null && badge.condition_publish_date_range !== '') {
        if (publishedAt) {
          var diffDays = (now - new Date(publishedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (diffDays > badge.condition_publish_date_range) continue;
        }
      }

      return badge;
    }
    return null;
  }

  function renderBadgeHtml(badge) {
    if (!badge) return '';
    // image_url is resolved server-side in main-collection-product-grid.liquid
    // (Liquid's image_url filter) since the raw metaobject reference only
    // serializes to a GID string, not a usable URL, when passed through `| json`.
    if (badge.image_url) {
      return (
        '<div class="flair"><img src="' +
        escapeHtml(badge.image_url) +
        '" alt="' +
        escapeHtml(badge.display_text || '') +
        '" width="75" height="75" loading="lazy"></div>'
      );
    }
    var fontWeight = badge.text_bold ? 'bold' : 'normal';
    return (
      '<div class="badge color-scheme-grey" style="background-color:' +
      escapeHtml(badge.background_color || '') +
      ';color:' +
      escapeHtml(badge.text_color || '') +
      ';font-weight:' +
      fontWeight +
      ';">' +
      escapeHtml(badge.display_text || '') +
      '</div>'
    );
  }

  function computeParentPrice(hit) {
    var minPrice = hit.variants_min_price;
    if (minPrice == null || minPrice <= 0) {
      return { display: 'Price Unavailable', compareAt: null, unresolved: true };
    }

    var maxPrice = hit.variants_max_price != null ? hit.variants_max_price : minPrice;
    var compareAtMax = hit.variants_compare_at_price_max || 0;
    var isMultiVariant = (hit.variants_count || 1) > 1 && maxPrice !== minPrice;
    var fromPrefix = isMultiVariant ? 'From ' : '';
    var onSale = compareAtMax > 0 && compareAtMax > minPrice;

    // "From " only ever prefixes the sale/selling price — the struck-through
    // compare-at is never prefixed, matching the instant search results page.
    return {
      display: fromPrefix + formatPrice(minPrice),
      compareAt: onSale ? formatPrice(compareAtMax) : null,
    };
  }

  // Parent mode: the lowest price above zero across a product's own variants,
  // in the shape computeParentPrice returns. Unpriced variants are skipped
  // entirely, compare-at included, so one of them cannot fake a markdown.
  // Null when no variant carries a price at all.
  function computeParentPriceFromVariants(data) {
    var variants = data && Array.isArray(data.variants) ? data.variants : [];
    var minPrice = null;
    var maxPrice = 0;
    var compareAtMax = 0;
    var pricedCount = 0;

    variants.forEach(function (variant) {
      // The product JSON reports money in cents, while every price Algolia
      // serves — and everything formatPrice takes — is already in dollars.
      var price = (variant.price || 0) / 100;
      if (price <= 0) return;
      pricedCount++;
      var compareAt = (variant.compare_at_price || 0) / 100;
      if (minPrice === null || price < minPrice) minPrice = price;
      if (price > maxPrice) maxPrice = price;
      if (compareAt > compareAtMax) compareAtMax = compareAt;
    });

    if (minPrice === null) return null;

    var allKnownPricesAgree = pricedCount >= 2 && maxPrice === minPrice;
    var showFromPrefix = variants.length > 1 && !allKnownPricesAgree;

    return {
      display: (showFromPrefix ? 'From ' : '') + formatPrice(minPrice),
      compareAt: compareAtMax > minPrice ? formatPrice(compareAtMax) : null,
    };
  }

  function renderParentPriceHtml(parentPrice) {
    if (parentPrice.compareAt) {
      return `
          <div class="price price--on-sale">
            <div class="price__container">
              <span class="price-item price-item--sale">
                ${parentPrice.display}
              </span>
              <span class="price-item price-item--regular">
                ${parentPrice.compareAt}
              </span>
            </div>
          </div>
        `;
    }
    return `
          <div class="price"${parentPrice.unresolved ? ' data-price-unresolved' : ''}>
            <div class="price__container">
              <span class="price-item price-item--regular">
                ${parentPrice.display}
              </span>
            </div>
          </div>
        `;
  }

  // Parent mode: swap the placeholder label on zero-min-price cards for the
  // product's real starting price. The product JSON is the same payload the
  // option hints already fetch, so a card that shows a hint costs no extra
  // request. Cards whose variants are all unpriced keep the label.
  function resolveZeroPriceCards(container) {
    var placeholders = container.querySelectorAll('[data-price-unresolved]');
    Array.prototype.forEach.call(placeholders, function (priceEl) {
      var wrapper = priceEl.closest('[data-product-handle]');
      var handle = wrapper && wrapper.getAttribute('data-product-handle');
      if (!handle) return;
      fetchProductData(handle).then(function (data) {
        var parentPrice = computeParentPriceFromVariants(data);
        if (!parentPrice) return;
        priceEl.outerHTML = renderParentPriceHtml(parentPrice);
      });
    });
  }

  function renderChildPriceHtml(price, compareAt) {
    if (compareAt && compareAt > price) {
      return (
        '<div class="price price--on-sale"><div class="price__container">' +
        '<span class="price-item price-item--regular">' +
        formatPrice(compareAt) +
        '</span>' +
        '<span class="price-item price-item--sale">' +
        formatPrice(price) +
        '</span></div></div>'
      );
    }
    return (
      '<div class="price"><div class="price__container">' +
      '<span class="price-item price-item--regular">' +
      formatPrice(price) +
      '</span></div></div>'
    );
  }

  function resolveZeroPriceChildCards(container) {
    var placeholders = container.querySelectorAll('[data-price-unresolved][data-variant-id]');
    Array.prototype.forEach.call(placeholders, function (priceEl) {
      var wrapper = priceEl.closest('[data-product-handle]');
      var handle = wrapper && wrapper.getAttribute('data-product-handle');
      var variantId = priceEl.getAttribute('data-variant-id');
      if (!handle || !variantId) return;
      fetchProductData(handle).then(function (data) {
        if (!data || !Array.isArray(data.variants)) return;
        var variant = data.variants.filter(function (v) {
          return String(v.id) === String(variantId);
        })[0];
        if (!variant) return;
        var price = (variant.price || 0) / 100;
        if (price <= 0) return;
        priceEl.outerHTML = renderChildPriceHtml(price, (variant.compare_at_price || 0) / 100);
      });
    });
  }

  // Child mode: append the selected variant's options to the parent title.
  function buildChildTitle(hit) {
    var parentTitle = hit.title || '';
    var options = [hit.option1, hit.option2, hit.option3].filter(function (o) {
      return o != null && o !== '' && o !== 'Default Title';
    });
    if (!options.length) return parentTitle;
    return parentTitle + ' – ' + options.join(', ');
  }

  function renderProductCard(hit, index) {
    // Validate hit object
    if (!hit) {
      console.warn('[Algolia Collection] renderProductCard called with null hit');
      return '';
    }

    var imageUrl = getHitImageSrc(hit);
    var imageSource = imageUrl === PLACEHOLDER_IMAGE ? 'shopify' : 'algolia';
    var price = hit.price?.amount || hit.price || 0;
    var compareAtPrice = hit.compare_at_price?.amount || hit.compare_at_price || 0;
    var handle = hit.handle || '';
    var url =
      listingMode === 0
        ? '/products/' + handle + '?variant=' + (hit.objectID || '')
        : hit.url || (handle ? '/products/' + handle : '#');
    var title = escapeHtml(listingMode === 0 ? buildChildTitle(hit) : hit.title || 'Untitled Product');
    var vendor = escapeHtml(hit.vendor || '');
    var productId = listingMode === 0 ? hit.objectID || hit.id || 'unknown' : hit.id || hit.objectID || 'unknown';
    var animationOrder = index + 1;
    // The sitewide scroll-trigger reveal (animations.js) only observes elements present at
    // DOMContentLoaded, so these AJAX-injected cards never get marked "offscreen" and instead
    // sit at the animation's initial opacity:0.01 indefinitely. Skip the animation classes
    // during a PLP scroll restore so the target card is actually visible once scrolled to.
    var skipRevealAnimation = document.documentElement.classList.contains('restoring-scroll-plp');
    var listingModeClass = listingMode === 0 ? ' algolia-collection-card--child' : '';
    // Render a previously-resolved hint inline so the card starts at its final
    // height; unseen products keep the collapsed placeholder and fill in lazily.
    var optionHintHtml = '';
    if (listingMode === 1) {
      var cachedHint = getOptionHintCache()[handle];
      if (cachedHint) {
        optionHintHtml =
          '<div class="product-card-options" data-option-hint data-hint-resolved>' + escapeHtml(cachedHint) + '</div>';
      } else if (cachedHint === '') {
        optionHintHtml =
          '<div class="product-card-options" data-option-hint data-hint-resolved style="display:none"></div>';
      } else {
        optionHintHtml = '<div class="product-card-options" data-option-hint style="display:none"></div>';
      }
    }

    // Badge
    var badgeHtml = renderBadgeHtml(getBadgeForHit(hit));

    // Check if quick view is disabled for this product
    var disableQuickView = hit.meta?.product?.disable_quick_view?.value || false;

    var priceHtml = '';
    if (listingMode === 1) {
      priceHtml = renderParentPriceHtml(computeParentPrice(hit));
    } else if (!price || price <= 0) {
      priceHtml = `
        <div class="price" data-price-unresolved data-variant-id="${escapeHtml(String(hit.objectID || ''))}">
          <div class="price__container">
            <span class="price-item price-item--regular">
              Price Unavailable
            </span>
          </div>
        </div>
      `;
    } else if (compareAtPrice && compareAtPrice > price) {
      priceHtml = `
        <div class="price price--on-sale">
          <div class="price__container">
            <span class="price-item price-item--regular">
              ${formatPrice(compareAtPrice)}
            </span>
            <span class="price-item price-item--sale">
              ${formatPrice(price)}
            </span>
          </div>
        </div>
      `;
    } else {
      priceHtml = `
        <div class="price">
          <div class="price__container">
            <span class="price-item price-item--regular">
              ${formatPrice(price)}
            </span>
          </div>
        </div>
      `;
    }

    // Quick view HTML (matches card-product-mag.liquid)
    var quickViewHtml = '';
    if (!disableQuickView) {
      quickViewHtml = `
        <div class="quick-add no-js-hidden color-scheme-light-linen">
          <modal-opener data-modal="#QuickAdd-${productId}">
            <button
              id="quick-add-algolia-${productId}-submit"
              type="submit"
              name="add"
              class="quick-add__submit button button--full-width button--secondary"
              aria-haspopup="dialog"
              data-product-url="${url}"
            >
              Quick View
              <div class="loading__spinner hidden">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  class="spinner"
                  viewBox="0 0 66 66"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </button>
          </modal-opener>
          <quick-add-modal id="QuickAdd-${productId}" class="quick-add-modal">
            <div
              role="dialog"
              aria-label="Choose options for ${title}"
              aria-modal="true"
              class="quick-add-modal__content global-settings-popup"
              tabindex="-1"
            >
              <button
                id="ModalClose-${productId}"
                type="button"
                class="quick-add-modal__toggle"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                  <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"/>
                </svg>
              </button>
              <div id="QuickAddInfo-${productId}" class="quick-add-modal__content-info"></div>
            </div>
          </quick-add-modal>
        </div>
      `;
    }

    var liClass = skipRevealAnimation
      ? `grid__item${listingModeClass}`
      : `grid__item scroll-trigger animate--slide-in${listingModeClass}`;
    var liStyle = skipRevealAnimation ? '' : ` style="--animation-order: ${animationOrder};"`;

    return `
      <li class="${liClass}"${liStyle}>
        <analytics-module data-module-name="productListing:grid">
          <div class="hit-wrapper card-wrapper product-card-wrapper underline-links-hover${listingModeClass}" data-product-handle="${handle}">
            <div class="hit-media card card--standard card--media" style="--ratio-percent: 100%;">
              <div class="card__inner ratio" style="--ratio-percent: 100%;">
                <div class="hit-image-link card__media">
                <a
                    href="${url}"
                    id="CardLink-Media-algolia-${productId}"
                    class="full-unstyled-link"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                  <div class="hit-image media media--transparent media--hover-effect" data-image-source="${imageSource}">
                    ${getResponsiveImageMarkup(imageUrl, title)}
                  </div>
                  </a>
                  ${quickViewHtml}
                </div>
                <div class="card__content">
                  <div class="card__information">
                    <h3 class="card__heading h5">
                      <a
                        href="${url}"
                        id="StandardCardNoMediaLink-algolia-${productId}"
                        class="full-unstyled-link"
                        data-analytics-link="productClick"
                        data-analytics-module="productListing:grid"
                        data-analytics-link-title="${title}"
                      >
                        ${title}
                      </a>
                    </h3>
                  </div>
                  ${badgeHtml}
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading h5" id="title-algolia-${productId}">
                    <a
                      href="${url}"
                      id="CardLink-Info-algolia-${productId}"
                      class="full-unstyled-link"
                      data-analytics-link="productClick"
                      data-analytics-module="productListing:grid"
                      data-analytics-link-title="${title}"
                    >
                      ${title}
                    </a>
                  </h3>
                  <div class="card-information">
                    ${vendor ? '<span class="visually-hidden">Vendor:</span><div class="caption-with-letter-spacing light">' + vendor + '</div>' : ''}
                    ${priceHtml}
                    ${optionHintHtml}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </analytics-module>
      </li>
    `;
  }

  function renderMarketingBlock(block, lazyLoad) {
    if (!block) return '';

    // Check if block is within date range
    if (!isMarketingBlockActive(block)) return '';

    var aspectRatio = block.aspect_ratio || 'portrait';
    var imageUrl = block.image_url || '';
    var imageWidth = block.image_width || 300;
    var imageHeight = block.image_height || 300;
    var imageAlt = escapeHtml(block.image_alt || '');
    var linkUrl = block.link_url || '';
    var position = block.grid_position || block.row || 0; // Use calculated position

    // Determine if image should be lazy loaded (only after position 2)
    // Matches Shopify native template behavior: forloop.index > 2
    var shouldLazyLoad = lazyLoad !== undefined ? lazyLoad : position > 2;

    // Determine image sizes based on aspect ratio
    var mobileSizes = aspectRatio === 'portrait' ? '250px' : '500px';
    var tabletSizes = aspectRatio === 'portrait' ? '200px' : '400px';
    var desktopSizes = aspectRatio === 'portrait' ? '300px' : '900px';

    // Build srcset for responsive images
    var srcset = '';
    if (imageUrl) {
      var widths = [300, 600, 900, 720, 940, 1066, 2000, 2400];
      srcset = widths
        .map(function (w) {
          return imageUrl.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w';
        })
        .join(', ');
    }

    var linkOpen = linkUrl ? '<a class="media" href="' + escapeHtml(linkUrl) + '">' : '';
    var linkClose = linkUrl ? '</a>' : '';

    // See renderProductCard for why the reveal animation is skipped during a PLP scroll restore.
    var skipRevealAnimation = document.documentElement.classList.contains('restoring-scroll-plp');
    var blockLiClass = skipRevealAnimation
      ? 'grid__item marketing-block--wrapper ' + aspectRatio
      : 'grid__item marketing-block--wrapper ' + aspectRatio + ' scroll-trigger animate--slide-in';

    // Build HTML using string concatenation for better compatibility
    var html = '<li class="' + blockLiClass + '">';
    html +=
      '<analytics-module data-module-name="productListing:marketingTile" data-module-position="' + position + '">';
    html += '<div class="media">';

    if (linkOpen) html += linkOpen;

    if (imageUrl) {
      html += '<img ';
      html += 'srcset="' + srcset + '" ';
      html += 'src="' + imageUrl + '" ';
      html +=
        'sizes="(min-width: 990px) ' + desktopSizes + ', (min-width: 750px) ' + tabletSizes + ', ' + mobileSizes + '" ';
      html += 'alt="' + imageAlt + '" ';
      html += 'loading="' + (shouldLazyLoad ? 'lazy' : 'eager') + '" ';
      html += 'width="' + imageWidth + '" ';
      html += 'height="' + imageHeight + '" ';
      html += '/>';
    }

    if (linkClose) html += linkClose;

    html += '</div>';
    html += '</analytics-module>';
    html += '</li>';

    return html;
  }

  function isMarketingBlockActive(block) {
    // Check if the marketing block is within its active date range
    if (!block.start_date && !block.end_date) return true;

    var now = new Date();

    if (block.start_date) {
      var startDate = new Date(block.start_date);
      if (now < startDate) return false;
    }

    if (block.end_date) {
      var endDate = new Date(block.end_date);
      if (now > endDate) return false;
    }

    return true;
  }

  // Facet state management (matching search page)
  var expandedFacets = {};
  var collapsedFacets = {};
  var FACET_VISIBLE_BY_DEFAULT = 10;
  // Off by default: variant-level index+ distinct makes per-value counts undercount products.
  var SHOW_FACET_COUNTS = !!(config && config.showFacetCounts === true);

  // Facet labels (matching search page)
  var FACET_LABELS = {
    'meta.filters.filter_product_category.fields.name': 'CATEGORY',
    'meta.filters.filter_product_type.fields.name': 'PRODUCT TYPE',
    price_range: 'PRICE RANGE',
    'meta.netsuite.color': 'COLOR',
    'options.size': 'SIZE',
    'options.fabric': 'FABRIC',
  };

  function facetLabel(attr) {
    if (FACET_LABELS[attr]) return FACET_LABELS[attr];
    var parts = attr.split('.');
    return parts[parts.length - 1].replace(/_/g, ' ').toUpperCase();
  }

  function renderFacets() {
    var container = document.getElementById('algolia-collection-facets');
    if (!container) return;

    var facets = lastResponse.facets || {};

    var html = '';

    // Render active filter pills in desktop sidebar and mobile (native Shopify style)
    renderActiveFilterPills();

    // Note: Current refinements are now handled by renderActiveFilterPills()
    // which renders native Shopify-style active facets pills in the sidebar and mobile

    // Define facet display order (matching search page)
    var facetConfig = [
      'meta.filters.filter_product_category.fields.name',
      'meta.filters.filter_product_type.fields.name',
      'price_range',
      'meta.netsuite.color',
      'options.size',
      'options.fabric',
    ];

    // Price sorting function
    var priceSortFn = function (a, b) {
      return parseFloat(a.split(':')[0]) - parseFloat(b.split(':')[0]);
    };

    var renderedCount = 0;
    facetConfig.forEach(function (facetAttr) {
      var facetData = facets[facetAttr];
      if (!facetData || Object.keys(facetData).length === 0) return;

      // Initialize collapsed state: first facet expanded, others collapsed
      if (collapsedFacets[facetAttr] === undefined) {
        collapsedFacets[facetAttr] = renderedCount > 0;
      }
      renderedCount++;

      var sortFn = facetAttr.indexOf('price') !== -1 ? priceSortFn : null;
      html += renderFacetGroup(facetAttr, facetData, sortFn);
    });

    container.innerHTML = html;
    bindFacetEvents();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Event Binding Helpers (must be before renderActiveFilterPills)
  // ───────────────────────────────────────────────────────────────────────────
  function bindClearAllButtons() {
    // Clear all filters button (desktop sidebar)
    var clearAllDesktopBtn = document.getElementById('algolia-clear-all-desktop');
    if (clearAllDesktopBtn) {
      // Remove old listener to prevent duplicates
      var newBtn = clearAllDesktopBtn.cloneNode(true);
      clearAllDesktopBtn.parentNode.replaceChild(newBtn, clearAllDesktopBtn);

      newBtn.addEventListener('click', function (e) {
        e.preventDefault();
        publishClickEvent('productListing:facet', 'clearAllFilters', 'filterClear');
        clearAllFilters();
        performSearch();
      });
    }

    // Clear all filters button (mobile)
    var clearAllMobileBtn = document.getElementById('algolia-clear-all-mobile');
    if (clearAllMobileBtn) {
      // Remove old listener to prevent duplicates
      var newMobileBtn = clearAllMobileBtn.cloneNode(true);
      clearAllMobileBtn.parentNode.replaceChild(newMobileBtn, clearAllMobileBtn);

      newMobileBtn.addEventListener('click', function (e) {
        e.preventDefault();
        publishClickEvent('productListing:facet', 'clearAllFilters', 'filterClear');
        clearAllFilters();
        performSearch();
      });
    }
  }

  function bindPillRemoveButtons() {
    // Remove individual filter pills (desktop and mobile)
    // Use event delegation to prevent duplicate event listeners
    var desktopPillsContainer = document.getElementById('algolia-active-filter-pills-desktop');
    var mobilePillsContainer = document.getElementById('algolia-active-filter-pills-mobile');

    [desktopPillsContainer, mobilePillsContainer].forEach(function (pillsContainer) {
      if (!pillsContainer) return;

      // Check if already bound to prevent duplicate listeners
      if (pillsContainer.hasAttribute('data-algolia-pills-bound')) {
        return;
      }

      // Mark as bound to prevent duplicate binding
      pillsContainer.setAttribute('data-algolia-pills-bound', 'true');

      // Use event delegation on the container to handle all pill clicks
      pillsContainer.addEventListener('click', function (e) {
        // Find the closest button element
        var btn = e.target.closest('.active-facets__button');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        var attr = btn.getAttribute('data-attr');
        var value = btn.getAttribute('data-value');

        if (!attr || !value) return;

        var linkTitle = attr.toLowerCase() + '::' + String(value).toLowerCase();
        publishClickEvent('productListing:facet', linkTitle, 'filterRemove');

        if (currentFilters[attr]) {
          currentFilters[attr] = currentFilters[attr].filter(function (v) {
            return v !== value;
          });
        }
        if (currentFilters[attr] && currentFilters[attr].length === 0) {
          delete currentFilters[attr];
        }
        currentPage = 0;

        // Perform search and ensure everything is properly initialized after
        performSearch().then(function () {
          // Ensure container visibility after filter removal
          ensureAlgoliaContainerVisible();
        });
      });
    });
  }

  function renderActiveFilterPills() {
    // Render for both desktop and mobile
    var desktopPillsContainer = document.getElementById('algolia-active-filter-pills-desktop');
    var mobilePillsContainer = document.getElementById('algolia-active-filter-pills-mobile');
    var desktopActiveFacetsContainer = document.getElementById('algolia-active-facets-desktop');
    var mobileActiveFacetsContainer = document.getElementById('algolia-active-facets-mobile');

    if (!hasActiveFilters()) {
      if (desktopPillsContainer) desktopPillsContainer.innerHTML = '';
      if (mobilePillsContainer) mobilePillsContainer.innerHTML = '';
      if (desktopActiveFacetsContainer) desktopActiveFacetsContainer.classList.remove('has-filters');
      if (mobileActiveFacetsContainer) mobileActiveFacetsContainer.classList.remove('has-filters');
      // Rebind clear all buttons even when no filters
      bindClearAllButtons();
      return;
    }

    // Add has-filters class to show clear all button
    if (desktopActiveFacetsContainer) desktopActiveFacetsContainer.classList.add('has-filters');
    if (mobileActiveFacetsContainer) mobileActiveFacetsContainer.classList.add('has-filters');

    // Build pills HTML
    var pillsHtml = '';
    Object.keys(currentFilters).forEach(function (attr) {
      currentFilters[attr].forEach(function (val) {
        var label = attr === 'price_range' ? '$' + val.replace(':', ' - $') : val;
        pillsHtml +=
          '<span class="algolia-facet-remove">' +
          '<a href="#" class="active-facets__button active-facets__button--light" data-attr="' +
          attr +
          '" data-value="' +
          escapeHtml(val) +
          '">' +
          '<span class="active-facets__button-inner button button--secondary">' +
          label +
          '<span class="svg-wrapper">' +
          // Matches the theme's icon-close-small.svg exactly (same markup facets.liquid
          // inlines for the native search page's filter pills), not a different hand-picked X.
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 12 13">' +
          '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/>' +
          '</svg>' +
          '</span>' +
          '<span class="visually-hidden">Remove filter</span>' +
          '</span>' +
          '</a>' +
          '</span>';
      });
    });

    // Update both containers. Note: the click listener in bindPillRemoveButtons()
    // is delegated on these persistent containers (never recreated, only their
    // innerHTML children are), so it must stay bound exactly once — removing
    // the bound flag here used to make bindPillRemoveButtons() attach another
    // listener on every filter change, firing publishClickEvent (and thus
    // webInteraction linkClicks) once per accumulated listener (MDX-2962).
    if (desktopPillsContainer) {
      desktopPillsContainer.innerHTML = pillsHtml;
    }
    if (mobilePillsContainer) {
      mobilePillsContainer.innerHTML = pillsHtml;
    }

    // Rebind event listeners after rendering new pills
    bindClearAllButtons();
    bindPillRemoveButtons();
  }

  function hasActiveFilters() {
    return Object.keys(currentFilters).some(function (facet) {
      return currentFilters[facet] && currentFilters[facet].length > 0;
    });
  }

  function renderFacetGroup(facetAttr, facetData, sortFn) {
    var keys = Object.keys(facetData);
    if (keys.length === 0) return '';
    if (sortFn) keys.sort(sortFn);

    var label = facetLabel(facetAttr);
    var isExpanded = expandedFacets[facetAttr];
    var isCollapsed = collapsedFacets[facetAttr];
    var visibleKeys = isExpanded ? keys : keys.slice(0, FACET_VISIBLE_BY_DEFAULT);
    var hasMore = keys.length > FACET_VISIBLE_BY_DEFAULT;

    var chevronSvg =
      '<svg class="algolia-filter-chevron' +
      (isCollapsed ? ' is-collapsed' : '') +
      '" viewBox="0 0 10 6" width="10" height="10" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

    var html = '<div class="algolia-filter-group">';
    html +=
      '<button class="algolia-filter-group__title" data-analytics-module="productListing:facet" data-analytics-link-title="' +
      (isCollapsed ? 'showMore' : 'showLess') +
      '" data-link-text="' +
      (isCollapsed ? 'expand' : 'collapse') +
      '" data-collapse-facet="' +
      facetAttr +
      '">' +
      label +
      chevronSvg +
      '</button>';

    if (!isCollapsed) {
      html += '<div class="algolia-filter-group__body">';
      html += '<ul class="algolia-filter-list">';
      visibleKeys.forEach(function (key) {
        var count = facetData[key];
        var isChecked = currentFilters[facetAttr] && currentFilters[facetAttr].indexOf(key) > -1;
        var displayLabel =
          key.indexOf(':') !== -1 && facetAttr.indexOf('price') !== -1
            ? '$' + key.split(':')[0] + ' - $' + key.split(':')[1]
            : facetAttr.indexOf('price') !== -1 && !isNaN(parseFloat(key))
              ? '$' + parseFloat(key).toFixed(2)
              : key;
        html += '<li class="algolia-filter-item">';
        html += '<label class="algolia-filter-label">';
        html +=
          '<input type="checkbox" class="algolia-filter-checkbox" data-attr="' +
          facetAttr +
          '" data-value="' +
          escapeHtml(key) +
          '"' +
          (isChecked ? ' checked' : '') +
          '>';
        html += '<span>' + displayLabel + '</span>';
        if (SHOW_FACET_COUNTS) html += '<span>(' + count + ')</span>';
        html += '</label>';
        html += '</li>';
      });
      html += '</ul>';

      if (hasMore) {
        var btnLabel = isExpanded ? '- Show less' : '+ Show more';
        html +=
          '<button class="algolia-filter-toggle" data-analytics-module="productListing:facet" data-analytics-link-title="' +
          (isExpanded ? 'showLess' : 'showMore') +
          '" data-link-text="' +
          (isExpanded ? 'collapse' : 'expand') +
          '" data-facet="' +
          facetAttr +
          '">' +
          btnLabel +
          '</button>';
      }
      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  function renderPagination() {
    var container = document.getElementById('algolia-collection-pagination');
    if (!container) return;

    var totalPages = lastResponse.nbPages;
    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    var html = '<div class="pagination-wrapper">';
    html += '<ul class="pagination__list list-unstyled" role="list">';

    // Previous button
    if (currentPage > 0) {
      html += '<li>';
      html +=
        '<button class="pagination__item pagination__item-arrow pagination__item--prev" data-page="' +
        (currentPage - 1) +
        '" aria-label="Previous page">';
      html += '<svg aria-hidden="true" focusable="false" class="icon icon-caret">';
      html += '<use href="#icon-caret" /></svg>';
      html += '</button>';
      html += '</li>';
    }

    // Page numbers (show current, neighbors, first, and last)
    var startPage = Math.max(0, currentPage - 2);
    var endPage = Math.min(totalPages - 1, currentPage + 2);

    // Always show first page
    if (startPage > 0) {
      html += '<li>';
      html += '<button class="pagination__item" data-page="0">1</button>';
      html += '</li>';
      if (startPage > 1) {
        html += '<li><span class="pagination__item">...</span></li>';
      }
    }

    // Show range around current page
    for (var i = startPage; i <= endPage; i++) {
      html += '<li>';
      html += '<button class="pagination__item' + (i === currentPage ? ' pagination__item--current' : '') + '" ';
      html += 'data-page="' + i + '" ' + (i === currentPage ? 'aria-current="page"' : '') + '>';
      html += i + 1;
      html += '</button>';
      html += '</li>';
    }

    // Always show last page
    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        html += '<li><span class="pagination__item">...</span></li>';
      }
      html += '<li>';
      html += '<button class="pagination__item" data-page="' + (totalPages - 1) + '">' + totalPages + '</button>';
      html += '</li>';
    }

    // Next button
    if (currentPage < totalPages - 1) {
      html += '<li>';
      html +=
        '<button class="pagination__item pagination__item-arrow pagination__item--next" data-page="' +
        (currentPage + 1) +
        '" aria-label="Next page">';
      html += '<svg aria-hidden="true" focusable="false" class="icon icon-caret">';
      html += '<use href="#icon-caret" /></svg>';
      html += '</button>';
      html += '</li>';
    }

    html += '</ul>';
    html += '</div>';
    container.innerHTML = html;
    bindPaginationEvents();
  }

  function renderProductCountBottom() {
    // Get Algolia container to scope queries
    var algoliaContainer = document.getElementById('algolia-collection-container');
    if (!algoliaContainer) return;

    // Query only within Algolia container to avoid native Shopify elements
    var container = algoliaContainer.querySelector('.algolia-product-count-bottom__text');
    if (!container) return;

    // Check if this element is ProductCountDesktop or ProductCountMobile
    // If so, skip updating it here since renderStats() already updated it
    // Support multiple ID formats: AlgoliaProductCount* (primary), algolia-ProductCount*, ProductCount*
    var isProductCountDesktop =
      container.id === 'AlgoliaProductCountDesktop' ||
      container.id === 'algolia-ProductCountDesktop' ||
      container.getAttribute('data-algolia-updated') === 'true';
    var isProductCountMobile =
      container.id === 'AlgoliaProductCountMobile' || container.id === 'algolia-ProductCountMobile';

    if (isProductCountDesktop || isProductCountMobile) {
      // Element already updated by renderStats(), skip to avoid overwriting
      return;
    }

    var totalHits = lastResponse.nbHits;

    // Calculate products visible on current page
    var startProduct = currentPage * hitsPerPage + 1;
    var endProduct = Math.min((currentPage + 1) * hitsPerPage, totalHits);
    // var productsOnPage = endProduct - startProduct + 1;

    var text = '';

    if (totalHits === 0) {
      container.textContent = '';
      return;
    }

    // Show "Showing X of Y products"
    // X = number of products visible on current page
    // Y = total products matching filters
    text = 'Showing ' + startProduct + '-' + endProduct + ' of ' + totalHits;

    container.textContent = text;

    // Force DOM reflow to ensure immediate visual update
    void container.offsetHeight;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ───────────────────────────────────────────────────────────────────────────
  /**
   * Update facet checkboxes to match the current filter state
   * Called after browser navigation or programmatic filter changes
   */
  function updateFacetCheckboxes() {
    var container = document.getElementById('algolia-collection-facets');
    if (!container) return;

    // Update all checkboxes based on currentFilters
    container.querySelectorAll('.algolia-filter-checkbox').forEach(function (checkbox) {
      var facet = checkbox.getAttribute('data-attr');
      var value = checkbox.getAttribute('data-value');
      var isActive = currentFilters[facet] && currentFilters[facet].indexOf(value) > -1;
      checkbox.checked = isActive;
    });
  }

  function bindFacetEvents() {
    var container = document.getElementById('algolia-collection-facets');
    if (!container) return;

    // Facet checkboxes (new class name matching search page)
    container.querySelectorAll('.algolia-filter-checkbox').forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        var facet = this.getAttribute('data-attr');
        var value = this.getAttribute('data-value');
        toggleFilter(facet, value, this.checked);
        currentPage = 0; // Reset to page 1

        var linkTitle = facet.toLowerCase() + '::' + String(value).toLowerCase();
        publishClickEvent('productListing:facet', linkTitle, this.checked ? 'filterApply' : 'filterRemove');

        performSearch();
      });
    });

    // Collapse/expand facet groups
    container.querySelectorAll('[data-collapse-facet]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var facet = btn.getAttribute('data-collapse-facet');
        collapsedFacets[facet] = !collapsedFacets[facet];
        renderFacets(); // Only re-render facets, not the entire grid
      });
    });

    // Show more/less toggle
    container.querySelectorAll('.algolia-filter-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var facet = btn.getAttribute('data-facet');
        expandedFacets[facet] = !expandedFacets[facet];
        renderFacets(); // Only re-render facets, not the entire grid
      });
    });

    // Bind clear all and pill remove buttons (now extracted to separate functions)
    bindClearAllButtons();
    bindPillRemoveButtons();

    // Empty state clear button (if exists)
    var clearBtn = document.getElementById('algolia-clear-filters');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        publishClickEvent('productListing:facet', 'clearAllFilters', 'filterClear');
        clearAllFilters();
        performSearch();
      });
    }

    // Ensure sort event is bound (re-bind to prevent any loss of listener)
    bindSortEvent();
  }

  function bindPaginationEvents() {
    document.querySelectorAll('.pagination__item[data-page]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var page = parseInt(this.getAttribute('data-page'), 10);
        currentPage = page;

        publishClickEvent('productListing:pagination', String(page + 1), 'paginate');

        performSearch().then(function () {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    });
  }

  /**
   * Update the sort dropdown to reflect the current sort state
   * Call this after any operation that might change currentSort or re-render the page
   */
  function updateSortDropdown() {
    var select = document.getElementById('algolia-sort-select');
    if (select && select.value !== currentSort) {
      select.value = currentSort;
    }
  }

  /**
   * Bind sort event listener to the sort dropdown
   * @param {boolean} force - Force rebinding even if already bound (for browser back navigation)
   */
  function bindSortEvent(force) {
    var select = document.getElementById('algolia-sort-select');
    if (!select) return;

    // Check if already bound to prevent duplicate listeners (unless forced)
    var alreadyBound = select.hasAttribute('data-algolia-sort-bound');
    if (alreadyBound && !force) {
      return;
    }

    // If forcing rebind, remove the old element and create a clean one
    if (force && alreadyBound) {
      var newSelect = select.cloneNode(true);
      newSelect.removeAttribute('data-algolia-sort-bound');
      if (select.parentNode) {
        select.parentNode.replaceChild(newSelect, select);
        select = newSelect;
      }
    }

    // Mark as bound to prevent duplicate binding
    select.setAttribute('data-algolia-sort-bound', 'true');

    // Track the sort dropdown being opened (native <select> has no reliable
    // cross-browser "open" event, so infer intent from the interactions that
    // precede showing the options), matching search page's sortOpen tracking.
    var isSortOpen = false;
    var handleSortOpen = function () {
      if (isSortOpen) return;
      isSortOpen = true;
      publishClickEvent('productListing:sort', 'sortOpen', 'open');
    };
    select.addEventListener('mousedown', handleSortOpen);
    select.addEventListener('touchstart', handleSortOpen, { passive: true });
    select.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        handleSortOpen();
      }
    });
    select.addEventListener('blur', function () {
      isSortOpen = false;
    });

    select.addEventListener('change', function (e) {
      e.preventDefault();
      e.stopPropagation();

      isSortOpen = false;

      // var previousSort = currentSort;
      currentSort = this.value;
      currentPage = 0; // Reset to page 1

      // Ensure Algolia container stays visible
      var shopifyContainer = document.getElementById('shopify-collection-grid');
      var algoliaContainer = document.getElementById('algolia-collection-container');
      if (shopifyContainer) shopifyContainer.style.display = 'none';
      if (algoliaContainer) algoliaContainer.style.display = 'block';

      // Get label for analytics
      var selectedOption = this.options[this.selectedIndex];
      var sortLabel = selectedOption ? selectedOption.text : this.value;

      publishClickEvent('productListing:sort', sortLabel, 'change');

      performSearch().then(function () {
        // Ensure container visibility after search completes
        ensureAlgoliaContainerVisible();
      });
    });
  }

  function bindSearchBox() {
    var input = document.getElementById('algolia-collection-searchbox');
    if (!input) return;

    var timeout;
    input.addEventListener('input', function () {
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        currentQuery = input.value.trim();
        currentPage = 0;
        performSearch();
      }, 300); // 300ms debounce
    });
  }

  var drawerPanel = 'main'; // 'main' | 'facet'
  var drawerFacetAttr = null;

  var FILTER_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-filter" viewBox="0 0 20 20"><path fill="currentColor" fill-rule="evenodd" d="M4.833 6.5a1.667 1.667 0 1 1 3.334 0 1.667 1.667 0 0 1-3.334 0M4.05 7H2.5a.5.5 0 0 1 0-1h1.55a2.5 2.5 0 0 1 4.9 0h8.55a.5.5 0 0 1 0 1H8.95a2.5 2.5 0 0 1-4.9 0m11.117 6.5a1.667 1.667 0 1 0-3.334 0 1.667 1.667 0 0 0 3.334 0M13.5 11a2.5 2.5 0 0 1 2.45 2h1.55a.5.5 0 0 1 0 1h-1.55a2.5 2.5 0 0 1-4.9 0H2.5a.5.5 0 0 1 0-1h8.55a2.5 2.5 0 0 1 2.45-2"></path></svg>';

  var SORT_OPTIONS = [
    { value: 'default', label: 'Featured' },
    { value: 'most_relevant', label: 'Most relevant' },
    { value: 'best_selling', label: 'Best selling' },
    { value: 'title_asc', label: 'Alphabetically, A-Z' },
    { value: 'title_desc', label: 'Alphabetically, Z-A' },
    { value: 'price_asc', label: 'Price, low to high' },
    { value: 'price_desc', label: 'Price, high to low' },
    { value: 'created_desc', label: 'Date, new to old' },
    { value: 'created_asc', label: 'Date, old to new' },
  ];

  function toTitleCase(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/(^|\s)\S/g, function (c) {
        return c.toUpperCase();
      });
  }

  function drawerHasFacets() {
    return !!(
      lastResponse &&
      lastResponse.facets &&
      Object.keys(lastResponse.facets).some(function (attr) {
        return Object.keys(lastResponse.facets[attr]).length > 0;
      })
    );
  }

  function formatItemCount() {
    var filtered = lastResponse ? lastResponse.nbHits : 0;
    var total = totalProductsInCollection || filtered;
    var noun = 'Item' + (total === 1 ? '' : 's');
    if (hasActiveFilters() && filtered !== total) {
      return filtered + ' of ' + total + ' ' + noun;
    }
    return total + ' ' + noun;
  }

  function buildMobileFilterButtonHtml() {
    var hasFacets = drawerHasFacets();
    return (
      '<span type="button" class="algolia-mobile-filter-button mobile-facets__open-label medium-hide large-up-hide caption-with-letter-spacing caption-with-letter-spacing--medium' +
      (hasFacets ? '' : ' algolia-mobile-filter-button--sort-only') +
      '">' +
      '<span class="algolia-mobile-filter-button__left">' +
      FILTER_ICON_SVG +
      '<span>' +
      (hasFacets ? 'FILTER AND SORT' : 'SORT BY') +
      '</span>' +
      '</span>' +
      '<span class="algolia-mobile-filter-button__count">' +
      formatItemCount() +
      '</span>' +
      '</span>'
    );
  }

  function renderMobileFilterButton() {
    var container = document.getElementById('algolia-mobile-filter-btn');
    if (!container) return;
    container.innerHTML = buildMobileFilterButtonHtml();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.algolia-mobile-filter-button');
    if (btn) {
      e.preventDefault();
      openMobileFilterDrawer();
    }
  });

  function ensureFilterDrawerElement() {
    var drawer = document.getElementById('algolia-mobile-filter-drawer');
    if (!drawer) {
      drawer = document.createElement('div');
      drawer.id = 'algolia-mobile-filter-drawer';
      drawer.className = 'algolia-mobile-filter-drawer';
      document.body.appendChild(drawer);
    }

    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
    return drawer;
  }

  function openMobileFilterDrawer() {
    drawerPanel = 'main';
    drawerFacetAttr = null;
    renderDrawerBody();
  }

  function renderDrawerMainPanel() {
    var facets = (lastResponse && lastResponse.facets) || {};
    var facetConfig = [
      'meta.filters.filter_product_category.fields.name',
      'meta.filters.filter_product_type.fields.name',
      'meta.netsuite.color',
      'price_range',
      'options.size',
      'options.fabric',
    ];

    // Native Shopify icon-arrow.svg (viewBox 14x10, right-pointing)
    var arrowSvg =
      '<span class="mobile-facets__arrow">' +
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10" aria-hidden="true">' +
      '<path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/>' +
      '</svg>' +
      '</span>';

    var html = '<div class="algolia-drawer__nav">';
    facetConfig.forEach(function (attr) {
      var facetData = facets[attr];
      if (!facetData || Object.keys(facetData).length === 0) return;
      var activeCount = currentFilters[attr] ? currentFilters[attr].length : 0;
      html +=
        '<button type="button" class="algolia-drawer__nav-row" data-open-facet="' +
        attr +
        '">' +
        '<div>' +
        '<span>' +
        toTitleCase(facetLabel(attr)) +
        '</span>' +
        (activeCount ? '<span class="algolia-drawer__nav-count">' + activeCount + '</span>' : '') +
        arrowSvg +
        '</div>' +
        '</button>';
    });
    html += '</div>';
    return html;
  }

  function renderDrawerFacetPanel(facetAttr) {
    var facets = (lastResponse && lastResponse.facets) || {};
    var facetData = facets[facetAttr];
    if (!facetData) return '';

    var keys = Object.keys(facetData);
    // Price sorts numerically by lower bound; everything else by popularity
    // (count descending), matching the desktop sidebar's own ordering.
    if (facetAttr.indexOf('price') !== -1) {
      keys.sort(function (a, b) {
        return parseFloat(a.split(':')[0]) - parseFloat(b.split(':')[0]);
      });
    } else {
      keys.sort(function (a, b) {
        return facetData[b] - facetData[a];
      });
    }

    var isExpanded = expandedFacets[facetAttr];
    var visibleKeys = isExpanded ? keys : keys.slice(0, FACET_VISIBLE_BY_DEFAULT);
    var hasMore = keys.length > FACET_VISIBLE_BY_DEFAULT;

    var html = '<ul class="algolia-filter-list algolia-drawer__facet-list">';
    visibleKeys.forEach(function (key) {
      var count = facetData[key];
      var isChecked = currentFilters[facetAttr] && currentFilters[facetAttr].indexOf(key) > -1;
      var displayLabel =
        key.indexOf(':') !== -1 && facetAttr.indexOf('price') !== -1
          ? '$' + key.split(':')[0] + ' - $' + key.split(':')[1]
          : facetAttr.indexOf('price') !== -1 && !isNaN(parseFloat(key))
            ? '$' + parseFloat(key).toFixed(2)
            : key;
      html += '<li class="algolia-filter-item">';
      html += '<label class="algolia-filter-label">';
      html +=
        '<input type="checkbox" class="algolia-filter-checkbox" data-attr="' +
        facetAttr +
        '" data-value="' +
        escapeHtml(key) +
        '"' +
        (isChecked ? ' checked' : '') +
        '>';
      html += '<span>' + displayLabel + (SHOW_FACET_COUNTS ? ' (' + count + ')' : '') + '</span>';
      html += '</label>';
      html += '</li>';
    });
    html += '</ul>';

    if (hasMore) {
      var btnLabel = isExpanded ? '- Show less' : '+ Show more';
      html +=
        '<button type="button" class="algolia-filter-toggle" data-analytics-module="productListing:facet" data-analytics-link-title="' +
        (isExpanded ? 'showLess' : 'showMore') +
        '" data-link-text="' +
        (isExpanded ? 'collapse' : 'expand') +
        '" data-facet="' +
        facetAttr +
        '">' +
        btnLabel +
        '</button>';
    }

    // Add individual facet footer with Clear and Apply buttons (native Shopify structure)
    var hasActiveFilters = currentFilters[facetAttr] && currentFilters[facetAttr].length > 0;
    html += '<div class="mobile-facets__footer gradient">';
    html += '<div class="mobile-facets__clear-wrapper">';
    html +=
      '<button type="button" class="mobile-facets__clear button button--secondary algolia-clear-facet' +
      (hasActiveFilters ? '' : ' disabled') +
      '" data-facet="' +
      facetAttr +
      '"' +
      (hasActiveFilters ? '' : ' disabled') +
      '>';
    html += 'Clear';
    html += '</button>';
    html += '</div>';
    html += '<button type="button" class="button button--primary algolia-apply-facet">';
    html += 'Apply';
    html += '</button>';
    html += '</div>';

    return html;
  }

  function renderDrawerSortHtml() {
    var html = '<div class="algolia-sort" data-sort-scope="mobile">';
    html += '<label class="algolia-sort__label">Sort by:</label>';
    html += '<select class="algolia-sort__select">';
    SORT_OPTIONS.forEach(function (opt) {
      var selected = opt.value === currentSort ? ' selected' : '';
      html += '<option value="' + opt.value + '"' + selected + '>' + escapeHtml(opt.label) + '</option>';
    });
    html += '</select>';
    html += '</div>';
    return html;
  }

  function renderDrawerBody() {
    var drawer = ensureFilterDrawerElement();
    var isFirstOpen = !drawer.classList.contains('is-open');
    var closeSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" width="24" height="24" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg>';

    var hasFacets = drawerHasFacets();

    var headerLeft;
    if (drawerPanel === 'facet' && drawerFacetAttr) {
      var backSvg =
        '<svg viewBox="0 0 8 14" width="8" height="14" aria-hidden="true"><path d="M7 1L1 7l6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      headerLeft =
        '<button type="button" class="algolia-drawer__back" aria-label="Back">' +
        backSvg +
        '</button>' +
        '<h3 class="algolia-drawer__title">' +
        toTitleCase(facetLabel(drawerFacetAttr)) +
        '</h3>';
    } else if (hasFacets) {
      headerLeft =
        '<span class="algolia-drawer__filter-icon">' +
        FILTER_ICON_SVG +
        '</span>' +
        '<h3 class="algolia-drawer__title">FILTER AND SORT</h3>';
    } else {
      headerLeft = '<h3 class="algolia-drawer__title">SORT BY</h3>';
    }

    var html = '<div class="algolia-drawer-overlay"></div>';
    html += '<div class="algolia-drawer' + (hasFacets ? '' : ' algolia-drawer--sort-only') + '">';
    html += '<div class="algolia-drawer__header">';
    html += '<div class="algolia-drawer__header-left">' + headerLeft + '</div>';
    html += '<span class="algolia-drawer__count">' + formatItemCount() + '</span>';
    html += '<button type="button" class="algolia-drawer__close" aria-label="Close">' + closeSvg + '</button>';
    html += '</div>';

    html += '<div class="algolia-drawer__body">';
    if (hasFacets) {
      html += drawerPanel === 'facet' ? renderDrawerFacetPanel(drawerFacetAttr) : renderDrawerMainPanel();
    }
    if (drawerPanel === 'main' || !hasFacets) {
      html += '<div class="algolia-drawer__sort">' + renderDrawerSortHtml() + '</div>';
    }

    // Main footer - only show on main panel (not on individual facet panels)
    if (drawerPanel === 'main' || !hasFacets) {
      var hasAnyActiveFilters = Object.keys(currentFilters).some(function (key) {
        return currentFilters[key] && currentFilters[key].length > 0;
      });

      html += '<div class="mobile-facets__footer">';
      if (hasFacets) {
        html += '<div class="mobile-facets__clear-wrapper">';
        html +=
          '<button type="button" id="MobileFacetsClearAll" class="mobile-facets__clear button button--secondary algolia-drawer__clear' +
          (hasAnyActiveFilters ? '' : ' disabled') +
          '"' +
          (hasAnyActiveFilters ? '' : ' disabled') +
          '>';
        html += 'Clear all filters';
        html += '</button>';
        html += '</div>';
      }
      html += '<button type="button" class="button button--primary algolia-drawer__apply">';
      html += 'Apply';
      html += '</button>';
      html += '</div>';
    }
    html += '</div>';
    html += '</div>';

    drawer.innerHTML = html;

    if (isFirstOpen) {
      // Reveal the container (display: block) first, off-screen, so the
      // browser has a painted frame to transition from.
      drawer.classList.add('is-open');
      void drawer.offsetHeight; // force reflow so the off-screen position is committed
      requestAnimationFrame(function () {
        // Adding is-active a frame later triggers the slide-in transition.
        drawer.classList.add('is-active');
      });
    }
    // On subsequent re-renders (facet navigation, filter changes, sort
    // changes) the drawer is already open - skip re-triggering the animation.

    document.body.style.overflow = 'hidden';

    bindDrawerEvents(drawer);
  }

  function bindDrawerEvents(drawer) {
    drawer.querySelector('.algolia-drawer__close').addEventListener('click', closeMobileFilterDrawer);
    drawer.querySelector('.algolia-drawer-overlay').addEventListener('click', closeMobileFilterDrawer);

    var backBtn = drawer.querySelector('.algolia-drawer__back');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        drawerPanel = 'main';
        drawerFacetAttr = null;
        renderDrawerBody();
      });
    }

    drawer.querySelectorAll('[data-open-facet]').forEach(function (row) {
      row.addEventListener('click', function () {
        drawerPanel = 'facet';
        drawerFacetAttr = row.getAttribute('data-open-facet');
        renderDrawerBody();
      });
    });

    drawer.querySelectorAll('.algolia-filter-checkbox').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var attr = cb.getAttribute('data-attr');
        var value = cb.getAttribute('data-value');
        toggleFilter(attr, value, cb.checked);
        currentPage = 0;

        var linkTitle = attr.toLowerCase() + '::' + String(value).toLowerCase();
        publishClickEvent('productListing:facet', linkTitle, cb.checked ? 'filterApply' : 'filterRemove');

        performSearch().then(function () {
          if (drawer.classList.contains('is-open')) renderDrawerBody();
        });
      });
    });

    drawer.querySelectorAll('.algolia-filter-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var facet = btn.getAttribute('data-facet');
        expandedFacets[facet] = !expandedFacets[facet];
        renderDrawerBody();
      });
    });

    var sortSelect = drawer.querySelector('.algolia-sort__select');
    if (sortSelect) {
      var isDrawerSortOpen = false;
      var handleDrawerSortOpen = function () {
        if (isDrawerSortOpen) return;
        isDrawerSortOpen = true;
        publishClickEvent('productListing:sort', 'sortOpen', 'open');
      };
      sortSelect.addEventListener('mousedown', handleDrawerSortOpen);
      sortSelect.addEventListener('touchstart', handleDrawerSortOpen, { passive: true });
      sortSelect.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          handleDrawerSortOpen();
        }
      });
      sortSelect.addEventListener('blur', function () {
        isDrawerSortOpen = false;
      });

      sortSelect.addEventListener('change', function (e) {
        isDrawerSortOpen = false;
        currentSort = e.target.value;
        currentPage = 0;

        var selectedOption = e.target.options[e.target.selectedIndex];
        publishClickEvent('productListing:sort', selectedOption ? selectedOption.text : e.target.value, 'change');

        var desktopSort = document.getElementById('algolia-sort-select');
        if (desktopSort) desktopSort.value = e.target.value;

        performSearch().then(function () {
          if (drawer.classList.contains('is-open')) renderDrawerBody();
        });
      });
    }

    // Main panel apply button (Show Items in main footer)
    var applyBtn = drawer.querySelector('.algolia-drawer__apply');
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        closeMobileFilterDrawer();
      });
    }

    // Clear all filters button (main footer)
    var clearBtn = drawer.querySelector('.algolia-drawer__clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (this.disabled || this.classList.contains('disabled')) return;
        publishClickEvent('productListing:facet', 'clearAllFilters', 'filterClear');
        currentFilters = {};
        currentPage = 0;
        performSearch().then(function () {
          if (drawer.classList.contains('is-open')) renderDrawerBody();
        });
      });
    }

    // Individual facet clear buttons (facet footer)
    drawer.querySelectorAll('.algolia-clear-facet').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (this.disabled || this.classList.contains('disabled')) return;
        var facet = this.getAttribute('data-facet');
        if (facet && currentFilters[facet]) {
          publishClickEvent('productListing:facet', facet.toLowerCase() + '::clear', 'filterClear');
          delete currentFilters[facet];
          currentPage = 0;
          performSearch().then(function () {
            if (drawer.classList.contains('is-open')) renderDrawerBody();
          });
        }
      });
    });

    // Individual facet apply buttons (Show Items in facet footer)
    var facetApplyBtns = drawer.querySelectorAll('.algolia-apply-facet');
    facetApplyBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Close the entire drawer (not just return to main panel)
        closeMobileFilterDrawer();
      });
    });
  }

  function closeMobileFilterDrawer() {
    var drawer = document.getElementById('algolia-mobile-filter-drawer');
    if (drawer) {
      // Remove is-active to trigger the slide-out transition; is-open stays
      // so the container is still displayed while it animates off-screen.
      drawer.classList.remove('is-active');

      // Wait for the transition to finish before hiding the container and
      // clearing its content - must match --duration-default (200ms).
      setTimeout(function () {
        drawer.classList.remove('is-open');
        drawer.innerHTML = '';
      }, 200);
    }

    // Comprehensive scroll lock cleanup
    document.body.style.overflow = '';
    document.body.style.removeProperty('overflow');
    document.documentElement.style.overflow = '';
    document.documentElement.style.removeProperty('overflow');

    // Remove any overflow classes that might have been added
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden-mobile');
    document.documentElement.classList.remove('overflow-hidden');

    // Force reflow to ensure styles are applied
    void document.body.offsetHeight;

    // Additional fallback cleanup after a short delay
    setTimeout(function () {
      document.body.style.overflow = '';
      document.body.style.removeProperty('overflow');
      document.documentElement.style.overflow = '';
      document.documentElement.style.removeProperty('overflow');
    }, 50);

    drawerPanel = 'main';
    drawerFacetAttr = null;
  }

  function toggleFilter(facet, value, isChecked) {
    if (!currentFilters[facet]) {
      currentFilters[facet] = [];
    }

    if (isChecked) {
      if (!currentFilters[facet].includes(value)) {
        currentFilters[facet].push(value);
      }
    } else {
      currentFilters[facet] = currentFilters[facet].filter(function (v) {
        return v !== value;
      });
    }

    // Remove facet key if empty
    if (currentFilters[facet].length === 0) {
      delete currentFilters[facet];
    }

    // Filter changes are reported solely as link click events by the caller's
    // publishClickEvent(); they must not raise a `search-event` .
  }

  function clearAllFilters() {
    currentFilters = {};
    currentPage = 0;
    currentQuery = '';

    var searchBox = document.getElementById('algolia-collection-searchbox');
    if (searchBox) searchBox.value = '';
  }

  // ───────────────────────────────────────────────────────────────────────────
  // URL State Management
  // ───────────────────────────────────────────────────────────────────────────
  function updateUrlState() {
    // Skip URL update if we're restoring from browser history
    // This prevents creating duplicate history entries during popstate
    if (isRestoringFromHistory) {
      return;
    }

    var params = new URLSearchParams();

    if (currentQuery) params.set('q', currentQuery);
    if (currentSort !== 'default') params.set('sort', currentSort);
    if (currentPage > 0) params.set('page', currentPage + 1); // 1-indexed for URL

    Object.keys(currentFilters).forEach(function (facet) {
      if (currentFilters[facet].length > 0) {
        params.set(facet, currentFilters[facet].join(','));
      }
    });

    var url = window.location.pathname;
    if (params.toString()) url += '?' + params.toString();

    // Create history entry with complete state for back/forward navigation
    var state = {
      filters: JSON.parse(JSON.stringify(currentFilters)),
      sort: currentSort,
      page: currentPage,
      query: currentQuery,
    };

    window.history.pushState(state, '', url);
  }

  function loadStateFromUrl() {
    var params = new URLSearchParams(window.location.search);

    currentQuery = params.get('q') || '';
    currentSort = params.get('sort') || 'default';
    currentPage = Math.max(0, parseInt(params.get('page') || '1', 10) - 1); // 0-indexed internally

    // Reset filters before loading from URL to avoid accumulation
    currentFilters = {};

    // List of URL parameters that should NOT be treated as filters
    // var ignoredParams = [
    //   'q',
    //   'sort',
    //   'page',
    //   'menu',
    //   'submenu',
    //   'menuheader',
    //   'submenuheader',
    //   'view',
    //   'preview_theme_id',
    //   'utm_source',
    //   'utm_medium',
    //   'utm_campaign',
    //   'utm_term',
    //   'utm_content',
    //   'gclid',
    //   'fbclid',
    //   'utm_id',
    //   'utm_name',
    //   'utm_cid',
    //   'utm_reader',
    //   'utm_voucher',
    //   'utm_creative',
    //   'utm_location',
    //   'utm_affiliate',
    //   'utm_referrer',
    //   'utm_social',
    //   'utm_placement',
    //   'utm_network',
    //   'utm_adgroup',
    //   'utm_ad',
    //   'utm_keyword',
    //   'utm_matchtype',
    //   'utm_device',
    //   'utm_adposition',
    //   'utm_feeditem',
    //   'utm_target',
    //   'utm_contentid',
    //   'utm_product',
    //   'utm_productid',
    //   'utm_productname',
    //   'utm_producttype',
    //   'utm_productcategory',
    //   'utm_productbrand',
    //   'utm_productvariant',
    //   'utm_productprice',
    //   'utm_productcurrency',
    //   'utm_productquantity',
    //   'utm_productavailability',
    //   'utm_productcondition',
    //   'utm_productcustom1',
    //   'utm_productcustom2',
    //   'utm_productcustom3',
    //   'utm_productcustom4',
    //   'utm_productcustom5',
    //   'utm_productcustom6',
    //   'utm_productcustom7',
    //   'utm_productcustom8',
    //   'utm_productcustom9',
    //   'utm_productcustom10',
    //   'smtrctid2',
    //   'smtrctid3',
    //   'smtrctid4',
    //   'smtrctid5',
    //   'smtrctid6',
    //   'smtrctid7',
    //   'smtrctid8',
    //   'smtrctid9',
    //   'smtrctid10',
    //   'srsltid',
    //   'srsltid2',
    //   'srsltid3',
    //   'srsltid4',
    //   'srsltid5',
    //   'srsltid6',
    //   'srsltid7',
    //   'srsltid8',
    //   'srsltid9',
    //   'srsltid10',
    //   'srsltid11',
    //   'srsltid12',
    //   'srsltid13',
    //   'srsltid14',
    //   'srsltid15',
    //   'srsltid16',
    //   'srsltid17',
    //   'srsltid18',
    //   'srsltid19',
    //   'srsltid20',
    //   'srsltid21',
    // ];

    var acceptedFacets = [
      'meta.filters.filter_product_category.fields.name',
      'meta.filters.filter_product_type.fields.name',
      'price_range',
      'meta.netsuite.color',
    ];

    params.forEach(function (value, key) {
      if (acceptedFacets.includes(key)) {
        currentFilters[key] = value.split(',');
      }
    });

    // Update sort dropdown
    var select = document.getElementById('algolia-sort-select');
    if (select) select.value = currentSort;

    // Update search box
    var searchBox = document.getElementById('algolia-collection-searchbox');
    if (searchBox) searchBox.value = currentQuery;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Marketing Blocks
  // ───────────────────────────────────────────────────────────────────────────
  function loadMarketingBlocks() {
    var dataEl = document.getElementById('collection-marketing-blocks-data');
    if (dataEl) {
      try {
        marketingBlocks = JSON.parse(dataEl.textContent);

        // Calculate actual grid positions using Shopify's formula
        // Formula: grid_index = (row * 6) - blocks_inserted + banners_inserted + 1
        calculateMarketingBlockPositions();
      } catch (e) {
        console.warn(e);
        marketingBlocks = [];
      }
    } else {
      marketingBlocks = [];
    }
  }

  function calculateMarketingBlockPositions() {
    // Implements Shopify's grid position calculation logic
    // Formula: grid_index = (row * 6) - blocks_inserted + banners_inserted + 1

    var blocksInserted = 0;
    var bannersInserted = 0;
    var previousGridIndex = 0;
    var pageSize = hitsPerPage || 48;

    marketingBlocks.forEach(function (block) {
      var row = parseInt(block.row, 10);

      // Check if block is within date range
      if (!isMarketingBlockActive(block)) {
        block.grid_position = 0; // 0 means skip this block
        return;
      }

      // Calculate grid position using Shopify formula
      var gridIndex = row * 6 - blocksInserted + bannersInserted + 1;

      // Check if we've moved to the next page
      var previousPageNumber = Math.floor(previousGridIndex / pageSize);
      var currentPageNumber = Math.floor(gridIndex / pageSize);

      // If on next page, reset offset counters
      if (previousPageNumber < currentPageNumber) {
        gridIndex = gridIndex + blocksInserted - bannersInserted;
        blocksInserted = 0;
        bannersInserted = 0;
      }

      // Store calculated position
      block.grid_position = gridIndex;

      // Update counters
      previousGridIndex = gridIndex;
      blocksInserted = blocksInserted + 1;
      if (block.aspect_ratio === 'landscape') {
        bannersInserted = bannersInserted + 1;
      }
    });
  }

  function getMarketingBlockAtPosition(position) {
    var block = marketingBlocks.find(function (block) {
      // Use calculated grid_position (which accounts for Shopify's formula)
      return block.grid_position === position;
    });

    return block;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Algolia Insights: result clicks + views
  //
  // Reuses window.AlgoliaInsights (algolia-insights-utils.js), the same shared
  // helper the search page records into — see its recordSearch()/publishClick()/
  // publishView() for the storage format this reads and writes.
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Saves the just-rendered grid as the live search record so a click or view
   * can look up its true (page-offset-aware) position and the objectID Algolia
   * indexed. Called once per settled render — after render() so facet/sort/
   * page changes always overwrite the previous record with the new queryID,
   * and a stale click can never attribute to a superseded search.
   *
   * No-ops silently when the response carries no queryID (clickAnalytics was
   * off, or this was a cached/empty/failed result) — recordSearch() itself
   * already guards on that, matching the search page's behavior.
   */
  function recordAlgoliaSearch() {
    if (!window.AlgoliaInsights || !lastResponse) return;
    // No explicit storage key: recordSearch() defaults it to indexName, which
    // is exactly what getSearchFor(indexName) below looks the record back up
    // by. A key is only needed when two queries on the SAME page could share
    // one index (search's ALL tab); a collection page only ever runs one.
    window.AlgoliaInsights.recordSearch(getIndexName(), lastResponse, currentPage, hitsPerPage);
  }

  /**
   * Publishes a view event for cards actually scrolled into the viewport,
   * once per queryID — not for every hit on page load. A fresh observer is
   * created per render because each render's cards are brand new DOM nodes;
   * the previous render's observer (and its pending, now-stale card
   * references) is disconnected first.
   */
  function observeCardsForViews(container) {
    if (!window.AlgoliaInsights || !lastResponse) return;

    if (cardViewObserver) {
      cardViewObserver.disconnect();
      cardViewObserver = null;
    }

    var cards = container.querySelectorAll('[data-object-id]');
    if (!cards.length) return;

    var indexName = getIndexName();

    function reportView() {
      var record = window.AlgoliaInsights.getSearchFor(indexName);
      if (!record || lastViewedQueryID === record.queryID) return;
      lastViewedQueryID = record.queryID;
      if (record.objectIDs.length === 0) return;
      window.AlgoliaInsights.publishView('collection', indexName, record.objectIDs);
    }

    if (!('IntersectionObserver' in window)) {
      // No viewport detection available — fall back to "rendered means seen"
      // rather than silently tracking nothing.
      reportView();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        var sawVisibleCard = entries.some(function (entry) {
          return entry.isIntersecting;
        });
        if (!sawVisibleCard) return;
        reportView();
        // Once per render's queryID is enough — stop watching every card.
        observer.disconnect();
        if (cardViewObserver === observer) cardViewObserver = null;
      },
      { threshold: 0.5 },
    );
    cardViewObserver = observer;
    cards.forEach(function (card) {
      observer.observe(card);
    });
  }

  // A rapid double-click lands two 'click' events on the same element before
  // the first navigation completes; dedupe by objectID+queryID within a
  // short window so it is reported once, matching the search page's
  // equivalent dedupe for add-to-cart in global.js.
  var DOUBLE_CLICK_WINDOW_MS = 1000;
  var lastClickKey = null;
  var lastClickAt = 0;

  function isDuplicateClick(objectID, queryID) {
    var key = objectID + '|' + queryID;
    var now = Date.now();
    if (lastClickKey === key && now - lastClickAt < DOUBLE_CLICK_WINDOW_MS) return true;
    lastClickKey = key;
    lastClickAt = now;
    return false;
  }

  // Delegated on document (survives re-renders) rather than bound per-card, so
  // facet/sort/page re-renders never need to rebind it and can't accumulate
  // duplicate listeners the way a per-card bind would.
  //
  // Position comes from the stored search record, not from the card's render
  // index — a card's index is relative to its own page, while Algolia expects
  // the absolute position across every page (spec: "page 3, card 4 is not
  // position 4"). getPositionOf() adds the record's page offset for exactly
  // that reason.
  document.addEventListener('click', function (e) {
    if (!window.AlgoliaInsights || !e.target.closest) return;

    var wrapper = e.target.closest('[data-object-id]');
    if (!wrapper) return;

    // Non-result interactions that happen to sit inside a card must never
    // count as a result click: carousel arrows/dots just cycle the image,
    // and quick-add/wishlist are separate conversions, not navigations to
    // the result itself.
    if (
      e.target.closest(
        '.hit-carousel__arrow, .hit-carousel__dot, .quick-add, modal-opener, quick-add-modal, magnolia-wishlist, .wishlist-btn',
      )
    ) {
      return;
    }
    if (!e.target.closest('a, button')) return;

    var objectID = wrapper.getAttribute('data-object-id');
    if (!objectID) return;

    var indexName = getIndexName();
    var record = window.AlgoliaInsights.getSearchFor(indexName);
    if (!record) return;

    var position = window.AlgoliaInsights.getPositionOf(record, objectID);
    if (!position) return;

    if (isDuplicateClick(objectID, record.queryID)) return;

    window.AlgoliaInsights.publishClick('collection', record.indexName, objectID, position, record.queryID);
  });

  // ───────────────────────────────────────────────────────────────────────────
  // Analytics
  // ───────────────────────────────────────────────────────────────────────────
  /**
   * Publish click analytics event (Path A: direct publish, no navigation).
   *
   * Publishes straight to the Shopify analytics bus, which the AEP pixel's
   * `page-click` subscriber turns into a `linkClicked` event with
   * webInteraction.region = `${moduleName}::${linkText}`. targetURL stays null
   * so the pixel treats every collection facet/sort/pagination interaction as
   * Path A rather than relaying it into the next page view — facet, sort and
   * paginate must not produce another page view event .
   *
   * @param {string} moduleName - Analytics module name (productListing:*)
   * @param {string} linkTitle - Link title
   * @param {string} linkText - Link text token
   */
  function publishClickEvent(moduleName, linkTitle, linkText) {
    if (!window.Shopify || !window.Shopify.analytics) return;

    var clickData = {
      moduleName: moduleName || '',
      linkTitle: linkTitle || '',
      linkText: linkText || '',
      pageType: getPageType(),
      targetURL: null,
      locURL: window.location.href,
    };

    try {
      window.Shopify.analytics.publish('page-click', { clickData: clickData });
    } catch (error) {
      console.error('[Algolia Collection Analytics] Failed to publish click event:', error);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Fallback Handler
  // ───────────────────────────────────────────────────────────────────────────
  function showNativeFallback() {
    var shopifyContainer = document.getElementById('shopify-collection-grid');
    var algoliaContainer = document.getElementById('algolia-collection-container');

    // Hide Algolia, show native Shopify
    if (algoliaContainer) {
      algoliaContainer.style.display = 'none';
    }

    if (shopifyContainer) {
      shopifyContainer.style.display = 'block';
    }

    // Native grid is server-rendered and already at full height, so scroll
    // restoration can proceed immediately.
    signalCollectionReady();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Initialization
  // ───────────────────────────────────────────────────────────────────────────
  function init() {
    // Validate Algolia config
    if (!config || !config.appId || config.appId.indexOf('REPLACE') === 0) {
      showNativeFallback();
      return; // Graceful degradation
    }

    // Check if we're on a collection page
    var collectionData = getCollectionData();
    if (!collectionData) {
      showNativeFallback();
      return;
    }

    collectionHandle = collectionData.handle;
    // collectionId = collectionData.id;
    hitsPerPage = collectionData.hitsPerPage;

    // Initialize search client
    var algoliaLib = window['algoliasearch/lite'];
    if (!algoliaLib) {
      showNativeFallback();
      return;
    }

    searchClient = algoliaLib.liteClient(config.appId, config.searchOnlyApiKey);

    // Load marketing blocks
    loadMarketingBlocks();

    // Load state from URL
    loadStateFromUrl();

    // Set initial history state
    var initialState = {
      filters: JSON.parse(JSON.stringify(currentFilters)),
      sort: currentSort,
      page: currentPage,
      query: currentQuery,
    };
    window.history.replaceState(initialState, '', window.location.href);

    // Handle browser back/forward navigation
    window.addEventListener('popstate', function (event) {
      // CRITICAL: Check if there's no state - this means we should navigate to previous URL
      // instead of trying to restore state from current URL
      if (!event.state) {
        // No previous state exists - user is navigating to a page without Algolia state
        // The browser will handle the navigation automatically, so we just need to ensure
        // proper cleanup and allow the default browser behavior
        console.log('No history state found - allowing browser to navigate to previous URL');
        return;
      }

      // Set flag to prevent creating duplicate history entries
      isRestoringFromHistory = true;

      if (event.state.filters !== undefined) {
        // Restore from history state
        currentFilters = JSON.parse(JSON.stringify(event.state.filters || {}));
        currentSort = event.state.sort || 'default';
        currentPage = event.state.page || 0;
        currentQuery = event.state.query || '';
      } else {
        // Fallback: parse from URL if state exists but doesn't have filters
        loadStateFromUrl();
      }

      // Update UI elements to reflect restored state
      var select = document.getElementById('algolia-sort-select');
      if (select) {
        select.value = currentSort;
        // Remove bound flag to allow rebinding after state restoration
        select.removeAttribute('data-algolia-sort-bound');
      }

      var searchBox = document.getElementById('algolia-collection-searchbox');
      if (searchBox) searchBox.value = currentQuery;

      // Perform search which will trigger render and re-bind all events
      performSearch()
        .then(function () {
          // After render completes, ensure all event listeners are active
          // CRITICAL: Force rebind sort event to handle browser back navigation
          // The sort dropdown might have been replaced or lost its listener
          bindSortEvent(true);

          // Update facet checkboxes to match restored filter state
          updateFacetCheckboxes();

          // CRITICAL: Ensure Algolia container stays visible after browser navigation
          // This prevents native Shopify code from interfering during popstate
          ensureAlgoliaContainerVisible();

          // CRITICAL: Force ProductCount update after browser navigation
          // Ensure stats are rendered even if render() was called earlier
          if (lastResponse) {
            renderStats();
          }

          // Reset flag after all rendering is complete
          isRestoringFromHistory = false;
        })
        .catch(function (err) {
          console.error('Error during browser back navigation:', err);
          // Reset flag even if search fails
          isRestoringFromHistory = false;
        });
    });

    // Bind events
    bindSortEvent();
    bindSearchBox();

    // Setup defensive MutationObserver to prevent theme code from hiding Algolia
    setupContainerVisibilityProtection();

    // Ensure Algolia container is visible on init
    ensureAlgoliaContainerVisible();

    var navEntries = (performance.getEntriesByType && performance.getEntriesByType('navigation')) || [];
    var isBackForward = !!navEntries[0] && navEntries[0].type === 'back_forward';

    var cachedResult = isBackForward ? loadPlpCache() : null;

    if (cachedResult) {
      lastResponse = cachedResult.response;
      totalProductsInCollection = cachedResult.totalProductsInCollection;

      totalProductsInCollectionMode = listingMode;
      render();
      ensureAlgoliaContainerVisible();
    } else {
      performSearch();
    }

  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
